import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  useContext,
  lazy,
  Suspense
} from 'react';
import ReactDOM from 'react-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  FaTrash, FaDownload, FaSync, FaRegCopy,
  FaCheck, FaTimes, FaCloudUploadAlt, FaChevronDown, FaChevronUp, FaPen
} from "react-icons/fa";
import { IoIosArrowDropdownCircle } from "react-icons/io";
import { GoInbox } from "react-icons/go";
import { MdWindow } from "react-icons/md";
import { PiImage } from "react-icons/pi";
import { FaFile, FaFilePdf, FaTooth } from "react-icons/fa";
import { Modal, Button, Form, InputGroup, ListGroup, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import ComTable from './ComTable';
import EnoTable from './EnoTable';
import ProcedureModel from './ProcedureModel';
import LazySTLThumbnail from './LazySTLThumbnail';
import { toast } from 'react-toastify';
import { useCurrency } from '../../../../../../context/currencyContext';
import CaseDataContext from '../../../../../../context/CaseDataContext';

import usePricingAccess from '../../../../../../hooks/usePricingAccess';
import { useUploadManager } from '../../../../../../context/UploadManagerContext';

const STLViewer = lazy(() => import('./STLViewer'));

const MemoComTable = React.memo(ComTable);
const MemoEnoTable = React.memo(EnoTable);
const MemoProcedureModel = React.memo(ProcedureModel);

const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

// ✅ FIX: Inline placeholder (data URI) - koi network request nahi, kabhi fail nahi hota.
// Pehle '/placeholder.png' use hota tha jo public folder mein nahi tha -> onError infinite loop
const PLACEHOLDER_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="140" height="140" viewBox="0 0 140 140">' +
      '<rect width="140" height="140" fill="#f1f3f5"/>' +
      '<path d="M40 95l22-26 16 18 12-14 20 22z" fill="#ced4da"/>' +
      '<circle cx="52" cy="52" r="9" fill="#ced4da"/>' +
      '</svg>'
  );

// ✅ FIX: onError loop se bachne ke liye - sirf ek baar placeholder lagao
const handleImgError = (e) => {
  const img = e.currentTarget;
  if (img.dataset.fallback === '1') return;
  img.dataset.fallback = '1';
  img.src = PLACEHOLDER_IMG;
};

const EMPTY_ARRAY = [];

const ALLOWED_EXTENSIONS = [
  '.png',
  '.jpg',
  '.jpeg',
  '.stl',
  '.ply',
  '.pdf',
  '.exe',
  '.zip',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.dcm'
];

const REPLACE_ACCEPT = {
  image: '.png,.jpg,.jpeg',
  stl: '.stl',
  other: ALLOWED_EXTENSIONS.join(',')
};

const rf = (isMobile, desktopSize, mobileSize) => {
  if (isMobile) return mobileSize || '10px';
  return desktopSize || '16px';
};

const getImageUrl = (path) => {
  if (!path) return PLACEHOLDER_IMG;
  const filename = path.split(/[\\/]/).pop();
  return `${API_BASE_URL}/uploads/${filename}`;
};

const isPlyFile = (file) => {
  const name = (file?.name || file?.path || '').toLowerCase();
  return name.endsWith('.ply');
};

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(query).matches
      : false
  );

  useEffect(() => {
    if (!window.matchMedia) return undefined;

    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);

    listener();

    if (media.addEventListener) {
      media.addEventListener('change', listener);
    } else {
      media.addListener(listener);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', listener);
      } else {
        media.removeListener(listener);
      }
    };
  }, [query]);

  return matches;
};

const modalBodyStyle = {
  padding: "20px",
};

const toothButtonStyle = {
  width: "30px",
  height: "40px",
  margin: "2px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "2px solid #ccc",
  borderRadius: "4px",
  fontWeight: "bold",
  fontSize: "14px",
  padding: 0,
};

const selectedToothStyle = {
  ...toothButtonStyle,
  backgroundColor: "#004aad",
  color: "white",
  borderColor: "#004aad",
};

const requiredLabelStyle = {
  display: "block",
  marginBottom: "5px",
  fontWeight: "bold",
  color: "#000000",
};

const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value >= 100 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
};

const formatDuration = (seconds) => {
  if (!isFinite(seconds) || seconds <= 0) return '0s';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const uploadStyles = `
  .cu-backdrop {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    background: rgba(10, 25, 55, 0.45);
    animation: cu-fade 0.2s ease-out;
  }
  .cu-backdrop.is-minimized {
    inset: auto;
    right: 16px;
    bottom: 16px;
    display: block;
    padding: 0;
    background: transparent;
    pointer-events: none;
    animation: none;
  }
  @keyframes cu-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  .cu-panel {
    position: relative;
    width: min(440px, calc(100vw - 24px));
    max-height: calc(100vh - 24px);
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border: 1px solid #d3e0f5;
    border-radius: 14px;
    box-shadow: 0 14px 44px rgba(0, 30, 90, 0.28);
    overflow: hidden;
    pointer-events: auto;
    animation: cu-in 0.25s ease-out;
  }
  @keyframes cu-in {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: none; }
  }
  .cu-panel.is-mini {
    width: min(290px, calc(100vw - 32px));
    border-radius: 12px;
    box-shadow: 0 8px 26px rgba(0, 30, 90, 0.3);
    animation: cu-mini-in 0.2s ease-out;
  }
  @keyframes cu-mini-in {
    from { opacity: 0; transform: translateY(10px) scale(0.96); }
    to { opacity: 1; transform: none; }
  }
  .cu-panel.is-mini .cu-head { padding: 8px 8px 8px 10px; gap: 9px; cursor: pointer; }
  .cu-panel.is-mini .cu-ring { width: 38px; height: 38px; flex: 0 0 38px; }
  .cu-panel.is-mini .cu-ring-txt { font-size: 10px; }
  .cu-panel.is-mini .cu-title,
  .cu-panel.is-mini .cu-sub {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .cu-panel.is-mini .cu-title { font-size: 13px; }
  .cu-panel.is-mini .cu-sub { font-size: 11px; margin-top: 2px; }
  .cu-panel.is-mini .cu-bar { height: 5px; }
  .cu-panel.is-mini .cu-foot { display: none; }
  .cu-head {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 12px 12px 14px;
    background: linear-gradient(135deg, #004aad 0%, #0a63d8 100%);
    color: #fff;
  }
  .cu-ring { position: relative; width: 54px; height: 54px; flex: 0 0 54px; }
  .cu-ring svg { transform: rotate(-90deg); display: block; width: 100%; height: 100%; }
  .cu-ring-bg { stroke: rgba(255, 255, 255, 0.28); }
  .cu-ring-fg { stroke: #fff; stroke-linecap: round; transition: stroke-dashoffset 0.25s ease; }
  .cu-ring-txt {
    position: absolute; inset: 0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 13px;
  }
  .cu-ring-txt svg { transform: none; width: 22px; height: 22px; }
  .cu-head-text { flex: 1 1 auto; min-width: 0; }
  .cu-title { font-weight: 700; font-size: 15px; line-height: 1.2; }
  .cu-sub { font-size: 12px; opacity: 0.92; margin-top: 3px; }
  .cu-icon-btn {
    flex: 0 0 auto;
    width: 28px; height: 28px;
    display: inline-flex; align-items: center; justify-content: center;
    background: rgba(255, 255, 255, 0.16);
    border: none; border-radius: 8px;
    color: #fff; cursor: pointer;
    transition: background 0.15s ease;
  }
  .cu-icon-btn:hover { background: rgba(255, 255, 255, 0.3); }
  .cu-bar { height: 8px; background: #e3ecfb; overflow: hidden; }
  .cu-bar-fill {
    height: 100%;
    background-color: #004aad;
    background-image: linear-gradient(
      45deg,
      rgba(255,255,255,0.28) 25%, transparent 25%,
      transparent 50%, rgba(255,255,255,0.28) 50%,
      rgba(255,255,255,0.28) 75%, transparent 75%
    );
    background-size: 28px 28px;
    animation: cu-stripes 0.8s linear infinite;
    transition: width 0.25s ease;
  }
  .cu-bar-fill.is-done { background-color: #1f9d55; animation: none; background-image: none; }
  .cu-bar-fill.is-warn { background-color: #d9822b; animation: none; background-image: none; }
  @keyframes cu-stripes { from { background-position: 0 0; } to { background-position: 28px 0; } }
  .cu-stats {
    display: flex; justify-content: space-between; gap: 8px;
    padding: 8px 14px;
    background: #f4f8fe;
    font-size: 12px; color: #3f5272;
  }
  .cu-list { max-height: min(260px, 40vh); overflow-y: auto; padding: 6px 8px; }
  .cu-row {
    display: grid; grid-template-columns: 20px 1fr auto;
    gap: 8px; align-items: center;
    padding: 6px; border-radius: 8px;
  }
  .cu-row:hover { background: #f4f8fe; }
  .cu-row-name {
    font-size: 12.5px; color: #1c2b45;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .cu-row-size { font-size: 11px; color: #6b7a93; }
  .cu-mini { height: 4px; margin-top: 4px; background: #e3ecfb; border-radius: 4px; overflow: hidden; }
  .cu-mini > span { display: block; height: 100%; background: #004aad; transition: width 0.2s ease; }
  .cu-mini.is-done > span { background: #1f9d55; }
  .cu-mini.is-error > span { background: #d64545; }
  .cu-status { font-size: 12px; font-weight: 600; min-width: 56px; text-align: right; color: #3f5272; }
  .cu-status.is-done { color: #1f9d55; }
  .cu-status.is-error { color: #d64545; }
  .cu-status.is-saving { color: #d9822b; }
  .cu-ico { display: inline-flex; align-items: center; justify-content: center; width: 20px; height: 20px; }
  .cu-ico.is-done { color: #1f9d55; }
  .cu-ico.is-error { color: #d64545; }
  .cu-ico.is-wait { color: #9aa8bf; }
  .cu-spin {
    width: 14px; height: 14px; border-radius: 50%;
    border: 2px solid #cfe0fa; border-top-color: #004aad;
    animation: cu-rot 0.7s linear infinite;
  }
  @keyframes cu-rot { to { transform: rotate(360deg); } }
  .cu-foot { display: flex; justify-content: flex-end; padding: 6px 12px 12px; }
  .cu-btn {
    border: 1px solid #c9d7ee; background: #fff; color: #1c2b45;
    border-radius: 8px; padding: 6px 14px; font-size: 13px; font-weight: 600; cursor: pointer;
  }
  .cu-btn:hover { background: #f4f8fe; }
  .cu-btn.is-danger { color: #c93636; border-color: #f0c4c4; }
  .cu-btn.is-danger:hover { background: #fff4f4; }
  .cu-btn.is-primary { background: #004aad; border-color: #004aad; color: #fff; }
  .cu-btn.is-primary:hover { background: #003a8a; }
  .cu-retry-btn {
    border: none; background: transparent; color: #004aad; cursor: pointer;
    font-size: 11px; font-weight: 600; padding: 0; margin-top: 2px; text-decoration: underline;
  }
  @media (prefers-reduced-motion: reduce) {
    .cu-backdrop, .cu-panel, .cu-panel.is-mini, .cu-bar-fill, .cu-spin { animation: none; }
  }
`;

const RING_RADIUS = 23;
const RING_CIRC = 2 * Math.PI * RING_RADIUS;

/* ============================================================
   DOWNLOAD PROGRESS PANEL
   ============================================================ */
const DownloadProgressPanel = ({
  items,
  startedAt,
  minimized,
  onToggle,
  onCancel,
  onClose,
  onRetry,
}) => {
  const [, setTick] = useState(0);

  const total = items.length;
  const doneCount = items.filter((i) => i.status === 'done').length;
  const failedCount = items.filter((i) => i.status === 'error').length;
  const canceledCount = items.filter((i) => i.status === 'canceled').length;
  const remaining = total - doneCount - failedCount - canceledCount;
  const finished = remaining === 0;

  useEffect(() => {
    if (finished) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [finished]);

  const totalBytes = items.reduce((s, i) => s + (i.size || 0), 0);
  const loadedBytes = items.reduce(
    (s, i) => s + (i.status === 'done' ? (i.size || 0) : Math.min(i.loaded || 0, i.size || 0)),
    0
  );
  const pct = totalBytes
    ? Math.min(100, Math.round((loadedBytes * 100) / totalBytes))
    : (finished && doneCount === total ? 100 : 0);

  const elapsed = Math.max((Date.now() - startedAt) / 1000, 0.001);
  const speed = loadedBytes / elapsed;
  const eta = speed > 0 ? Math.max(0, (totalBytes - loadedBytes) / speed) : 0;

  const noun = total === 1 ? 'file' : 'files';
  let title = `Downloading ${total} ${noun}`;
  if (finished) {
    if (failedCount > 0) title = 'Finished with errors';
    else if (canceledCount > 0) title = 'Download canceled';
    else title = total === 1 ? 'Download complete' : 'Downloads complete';
  }

  const subtitle = finished
    ? `${doneCount} of ${total} ${noun} downloaded${failedCount ? ` · ${failedCount} failed` : ''}${canceledCount ? ` · ${canceledCount} canceled` : ''}`
    : `${doneCount} of ${total} downloaded · ${remaining} remaining`;

  const barClass = finished ? (failedCount || canceledCount ? 'is-warn' : 'is-done') : '';
  const ringOffset = RING_CIRC * (1 - pct / 100);
  const isSuccess = finished && failedCount === 0 && canceledCount === 0;

  return ReactDOM.createPortal(
    <div className={`cu-backdrop${minimized ? ' is-minimized' : ''}`}>
      <div className={`cu-panel${minimized ? ' is-mini' : ''}`} role="status" aria-live="polite">
        <div
          className="cu-head"
          onClick={minimized ? onToggle : undefined}
          title={minimized ? 'Click to expand' : undefined}
        >
          <div className="cu-ring">
            <svg width="54" height="54" viewBox="0 0 54 54">
              <circle className="cu-ring-bg" cx="27" cy="27" r={RING_RADIUS} fill="none" strokeWidth="5" />
              <circle
                className="cu-ring-fg"
                cx="27" cy="27" r={RING_RADIUS}
                fill="none" strokeWidth="5"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={isSuccess ? 0 : ringOffset}
                style={isSuccess ? { stroke: '#ffffff' } : undefined}
              />
            </svg>
            <div className="cu-ring-txt">
              {isSuccess ? (
                <FaCheck style={{ width: '22px', height: '22px', color: '#ffffff' }} />
              ) : (
                `${pct}%`
              )}
            </div>
          </div>

          <div className="cu-head-text">
            <div className="cu-title">{title}</div>
            <div className="cu-sub">{subtitle}</div>
          </div>

          <button
            type="button"
            className="cu-icon-btn"
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            title={minimized ? 'Show details' : 'Hide details'}
            aria-label={minimized ? 'Show download details' : 'Hide download details'}
          >
            {minimized ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
          </button>

          {finished && (
            <button
              type="button"
              className="cu-icon-btn"
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              title="Close"
              aria-label="Close"
            >
              <FaTimes size={12} />
            </button>
          )}
        </div>

        <div className="cu-bar">
          <div className={`cu-bar-fill ${barClass}`} style={{ width: `${pct}%` }} />
        </div>

        {!minimized && (
          <>
            <div className="cu-stats">
              <span>{formatBytes(loadedBytes)} / {formatBytes(totalBytes)}</span>
              {finished ? (
                <span>Took {formatDuration(elapsed)}</span>
              ) : (
                <span>{formatBytes(speed)}/s · {formatDuration(eta)} left</span>
              )}
            </div>

            <div className="cu-list">
              {items.map((item) => {
                const itemPct = item.size
                  ? Math.min(100, Math.round(((item.loaded || 0) * 100) / item.size))
                  : (item.status === 'done' ? 100 : 0);

                let statusText = 'Waiting';
                let statusClass = '';
                if (item.status === 'downloading') {
                  statusText = `${itemPct}%`;
                } else if (item.status === 'saving') {
                  statusText = 'Saving…';
                  statusClass = 'is-saving';
                } else if (item.status === 'done') {
                  statusText = 'Done';
                  statusClass = 'is-done';
                } else if (item.status === 'error') {
                  statusText = 'Failed';
                  statusClass = 'is-error';
                } else if (item.status === 'canceled') {
                  statusText = 'Canceled';
                }

                return (
                  <div className="cu-row" key={item.id} title={item.error || item.name}>
                    <span className={`cu-ico ${item.status === 'done' ? 'is-done' : item.status === 'error' ? 'is-error' : 'is-wait'}`}>
                      {(item.status === 'downloading' || item.status === 'saving') && <span className="cu-spin" />}
                      {item.status === 'done' && <FaCheck size={12} />}
                      {item.status === 'error' && <FaTimes size={12} />}
                      {(item.status === 'queued' || item.status === 'canceled') && <FaDownload size={13} />}
                    </span>

                    <div style={{ minWidth: 0 }}>
                      <div className="cu-row-name">{item.name}</div>
                      <div className="cu-row-size">
                        {item.status === 'error' && item.error ? item.error : formatBytes(item.size || 0)}
                      </div>
                      <div className={`cu-mini ${item.status === 'done' ? 'is-done' : item.status === 'error' ? 'is-error' : ''}`}>
                        <span style={{ width: `${item.status === 'done' ? 100 : itemPct}%` }} />
                      </div>
                      {item.status === 'error' && onRetry && (
                        <button
                          type="button"
                          className="cu-retry-btn"
                          onClick={(e) => { e.stopPropagation(); onRetry(item.id); }}
                        >
                          Retry
                        </button>
                      )}
                    </div>

                    <span className={`cu-status ${statusClass}`}>{statusText}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="cu-foot">
          {finished ? (
            <button type="button" className="cu-btn is-primary" onClick={onClose}>Done</button>
          ) : (
            <button type="button" className="cu-btn is-danger" onClick={onCancel}>Cancel download</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const UploadProgressPanel = ({ items, kind, startedAt, minimized, onToggle, onCancel, onClose }) => {
  const [, setTick] = useState(0);

  const total = items.length;
  const doneCount = items.filter((i) => i.status === 'done').length;
  const failedCount = items.filter((i) => i.status === 'error').length;
  const canceledCount = items.filter((i) => i.status === 'canceled').length;
  const remaining = total - doneCount - failedCount - canceledCount;
  const finished = remaining === 0;

  useEffect(() => {
    if (finished) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 500);
    return () => clearInterval(t);
  }, [finished]);

  const totalBytes = items.reduce((s, i) => s + i.size, 0);
  const loadedBytes = items.reduce(
    (s, i) => s + (i.status === 'done' ? i.size : Math.min(i.loaded, i.size)),
    0
  );
  const pct = totalBytes ? Math.min(100, Math.round((loadedBytes * 100) / totalBytes)) : 0;

  const elapsed = Math.max((Date.now() - startedAt) / 1000, 0.001);
  const speed = loadedBytes / elapsed;
  const eta = speed > 0 ? (totalBytes - loadedBytes) / speed : 0;

  const noun = total === 1 ? 'file' : 'files';
  let title = kind === 'replace' ? 'Replacing file' : `Uploading ${total} ${noun}`;
  if (finished) {
    if (failedCount > 0) title = 'Finished with errors';
    else if (canceledCount > 0) title = 'Upload canceled';
    else title = kind === 'replace' ? 'File replaced' : 'Upload complete';
  }

  const subtitle = finished
    ? `${doneCount} of ${total} ${noun} uploaded${failedCount ? ` · ${failedCount} failed` : ''}${canceledCount ? ` · ${canceledCount} canceled` : ''}`
    : `${doneCount} of ${total} uploaded · ${remaining} remaining`;

  const barClass = finished ? (failedCount || canceledCount ? 'is-warn' : 'is-done') : '';
  const ringOffset = RING_CIRC * (1 - pct / 100);

  const isSuccess = finished && failedCount === 0 && canceledCount === 0;

  return ReactDOM.createPortal(
    <div className={`cu-backdrop${minimized ? ' is-minimized' : ''}`}>
      <div className={`cu-panel${minimized ? ' is-mini' : ''}`} role="status" aria-live="polite">
        <div
          className="cu-head"
          onClick={minimized ? onToggle : undefined}
          title={minimized ? 'Click to expand' : undefined}
        >
          <div className="cu-ring">
            <svg width="54" height="54" viewBox="0 0 54 54">
              <circle className="cu-ring-bg" cx="27" cy="27" r={RING_RADIUS} fill="none" strokeWidth="5" />
              <circle
                className="cu-ring-fg"
                cx="27" cy="27" r={RING_RADIUS}
                fill="none" strokeWidth="5"
                strokeDasharray={RING_CIRC}
                strokeDashoffset={isSuccess ? 0 : ringOffset}
                style={isSuccess ? { stroke: '#ffffff' } : undefined}
              />
            </svg>
            <div className="cu-ring-txt">
              {isSuccess ? (
                <FaCheck style={{ width: '22px', height: '22px', color: '#ffffff' }} />
              ) : (
                `${pct}%`
              )}
            </div>
          </div>

          <div className="cu-head-text">
            <div className="cu-title">{title}</div>
            <div className="cu-sub">{subtitle}</div>
          </div>

          <button
            type="button"
            className="cu-icon-btn"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            title={minimized ? 'Show details' : 'Hide details'}
            aria-label={minimized ? 'Show upload details' : 'Hide upload details'}
          >
            {minimized ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
          </button>

          {finished && (
            <button
              type="button"
              className="cu-icon-btn"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              title="Close"
              aria-label="Close"
            >
              <FaTimes size={12} />
            </button>
          )}
        </div>

        <div className="cu-bar">
          <div className={`cu-bar-fill ${barClass}`} style={{ width: `${pct}%` }} />
        </div>

        {!minimized && (
          <>
            <div className="cu-stats">
              <span>{formatBytes(loadedBytes)} / {formatBytes(totalBytes)}</span>
              {finished ? (
                <span>Took {formatDuration(elapsed)}</span>
              ) : (
                <span>{formatBytes(speed)}/s · {formatDuration(eta)} left</span>
              )}
            </div>

            <div className="cu-list">
              {items.map((item) => {
                const itemPct = item.size
                  ? Math.min(100, Math.round((item.loaded * 100) / item.size))
                  : 0;
                const saving = item.status === 'uploading' && item.loaded >= item.size;

                let statusText = 'Waiting';
                let statusClass = '';
                if (item.status === 'uploading') {
                  statusText = saving ? 'Saving…' : `${itemPct}%`;
                  statusClass = saving ? 'is-saving' : '';
                } else if (item.status === 'done') {
                  statusText = 'Done';
                  statusClass = 'is-done';
                } else if (item.status === 'error') {
                  statusText = 'Failed';
                  statusClass = 'is-error';
                } else if (item.status === 'canceled') {
                  statusText = 'Canceled';
                }

                return (
                  <div className="cu-row" key={item.id} title={item.error || item.name}>
                    <span className={`cu-ico ${item.status === 'done' ? 'is-done' : item.status === 'error' ? 'is-error' : 'is-wait'}`}>
                      {item.status === 'uploading' && <span className="cu-spin" />}
                      {item.status === 'done' && <FaCheck size={12} />}
                      {item.status === 'error' && <FaTimes size={12} />}
                      {(item.status === 'queued' || item.status === 'canceled') && <FaCloudUploadAlt size={14} />}
                    </span>

                    <div style={{ minWidth: 0 }}>
                      <div className="cu-row-name">{item.name}</div>
                      <div className="cu-row-size">
                        {item.status === 'error' && item.error ? item.error : formatBytes(item.size)}
                      </div>
                      <div className={`cu-mini ${item.status === 'done' ? 'is-done' : item.status === 'error' ? 'is-error' : ''}`}>
                        <span style={{ width: `${item.status === 'done' ? 100 : itemPct}%` }} />
                      </div>
                    </div>

                    <span className={`cu-status ${statusClass}`}>{statusText}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <div className="cu-foot">
          {finished ? (
            <button type="button" className="cu-btn is-primary" onClick={onClose}>Done</button>
          ) : (
            <button type="button" className="cu-btn is-danger" onClick={onCancel}>Cancel upload</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const FileIcon = () => (
  <svg
    width="19"
    height="19"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M8 13h8M8 17h6" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21a8 8 0 0 0-16 0" />
    <circle cx="12" cy="8" r="4" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const STLFullViewModal = React.memo(function STLFullViewModal({
  file,
  files,
  fileUrl,
  fileUrls,
  onClose,
  getResponsiveFontSize,
}) {
  const [isModelReady, setIsModelReady] = useState(false);
  const [progress, setProgress] = useState(0);

  const isMulti = Array.isArray(fileUrls) && fileUrls.length > 0;

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };

    window.addEventListener("keydown", handleEsc);

    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleLoad = useCallback(() => setIsModelReady(true), []);
  const handleError = useCallback(() => setIsModelReady(true), []);

  const formatUploadTime = () => {
    const raw =
      file?.date ||
      file?.createdAt ||
      file?.uploadedAt ||
      file?.updatedAt;

    if (!raw) return "Unknown";

    try {
      const d = new Date(raw);

      if (isNaN(d.getTime())) return "Unknown";

      return d.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Unknown";
    }
  };

  const fileName = isMulti
    ? `${fileUrls.length} files selected`
    : (file?.name ||
       (file?.path ? file.path.split(/[\\/]/).pop() : "") ||
       "File");

  const uploadedBy = isMulti
    ? "Multiple"
    : (file?.uploadedBy ||
       file?.uploaded_by ||
       file?.uploaderName ||
       file?.uploader ||
       file?.createdBy ||
       file?.userName ||
       "Unknown User");

  const uploadTime = isMulti ? "—" : formatUploadTime();

  const responsive = (desktop, mobile) =>
    getResponsiveFontSize(desktop, mobile);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(0, 0, 0, 0.85)",
        zIndex: 9999,
        padding: "6px",
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(1800px, 96vw)",
          height: "min(900px, 92vh)",
          backgroundColor: "#00a1d6",
          borderRadius: "10px",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            backgroundColor: "#004aad",
            backgroundImage:
              "linear-gradient(115deg, #004aad 0%, #003b88 100%)",
            color: "#fff",
            padding: responsive("14px 22px", "10px 12px"),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: responsive("20px", "10px"),
            flexShrink: 0,
            minHeight: responsive("76px", "54px"),
            boxSizing: "border-box",
            borderBottom: "1px solid rgba(255,255,255,0.18)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: responsive("12px", "8px"),
              minWidth: 0,
              flex: "1 1 auto",
            }}
          >
            <div
              style={{
                width: responsive("42px", "34px"),
                height: responsive("42px", "34px"),
                minWidth: responsive("42px", "34px"),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.13)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "10px",
              }}
            >
              <FileIcon />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  fontSize: responsive("17px", "12px"),
                  fontWeight: 700,
                  lineHeight: 1.35,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  letterSpacing: "0.1px",
                }}
                title={fileName}
              >
                {fileName}
              </div>

              <div
                style={{
                  fontSize: responsive("11px", "9px"),
                  color: "rgba(255,255,255,0.72)",
                  letterSpacing: "0.7px",
                  textTransform: "uppercase",
                  fontWeight: 500,
                }}
              >
                {isMulti ? `Multi-Model Viewer · ${fileUrls.length} files` : "3D Model Viewer"}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: responsive("20px", "8px"),
              flexShrink: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: responsive("9px", "5px"),
                minWidth: 0,
                maxWidth: responsive("220px", "100px"),
              }}
              title={`Uploaded by: ${uploadedBy}`}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                <UserIcon />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    fontSize: responsive("10px", "8px"),
                    color: "rgba(255,255,255,0.68)",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Uploaded By
                </span>

                <span
                  style={{
                    fontSize: responsive("13px", "10px"),
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {uploadedBy}
                </span>
              </div>
            </div>

            <div
              style={{
                width: "1px",
                height: responsive("34px", "26px"),
                backgroundColor: "rgba(255,255,255,0.25)",
                flexShrink: 0,
              }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: responsive("9px", "5px"),
                flexShrink: 0,
              }}
              title={`Uploaded: ${uploadTime}`}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                <ClockIcon />
              </div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                }}
              >
                <span
                  style={{
                    fontSize: responsive("10px", "8px"),
                    color: "rgba(255,255,255,0.68)",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Uploaded At
                </span>

                <span
                  style={{
                    fontSize: responsive("12px", "9px"),
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {uploadTime}
                </span>
              </div>
            </div>

            <button
              onClick={onClose}
              aria-label="Close STL viewer"
              title="Close viewer"
              style={{
                backgroundColor: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "8px",
                width: responsive("36px", "28px"),
                height: responsive("36px", "28px"),
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: responsive("18px", "13px"),
                cursor: "pointer",
                flexShrink: 0,
                transition:
                  "background-color 0.2s ease, transform 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255,255,255,0.28)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor =
                  "rgba(255,255,255,0.12)";
              }}
            >
              &#10005;
            </button>
          </div>
        </div>

        <div
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            width: "100%",
          }}
        >
          {!isModelReady && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#00a1d6",
                zIndex: 5,
              }}
            >
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  border: "4px solid rgba(255,255,255,0.3)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "stl-modal-spin 0.8s linear infinite",
                }}
              />

              <p
                style={{
                  color: "#fff",
                  marginTop: "15px",
                  fontSize: responsive("16px", "12px"),
                }}
              >
                Loading 3D Model{isMulti ? "s" : ""}...
                {progress > 0 && progress < 100
                  ? ` ${progress}%`
                  : ""}
              </p>

              <style>
                {`
                  @keyframes stl-modal-spin {
                    to {
                      transform: rotate(360deg);
                    }
                  }
                `}
              </style>
            </div>
          )}

          <div
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            <Suspense fallback={null}>
              <STLViewer
                fileUrl={isMulti ? undefined : fileUrl}
                fileUrls={isMulti ? fileUrls : undefined}
                size="extra-large"
                shouldLoad={true}
                onProgress={setProgress}
                onLoad={handleLoad}
                onError={handleError}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
  );
});

const ProcedureSelectorModal = ({
  show,
  handleClose,
  onSelect,
  allProcedures,
  accountId,
  hasPricingAccess = false,
}) => {
  const { formatAmount } = useCurrency();
  const [searchTerm, setSearchTerm] = useState("");
  const frequentlyUsedProcedureIds = ["1093", "1035"];

  const isMobile = useMediaQuery('(max-width: 767px)');

  const filteredProcedures = React.useMemo(() => {
    if (!searchTerm) {
      return allProcedures.filter(
        (proc) => !accountId || proc.accountId === accountId
      );
    }

    return allProcedures.filter(
      (proc) =>
        proc.label.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (!accountId || proc.accountId === accountId)
    );
  }, [searchTerm, allProcedures, accountId]);

  const frequentlyUsed = filteredProcedures.filter((p) =>
    frequentlyUsedProcedureIds.includes(p.procedureId)
  );

  const generalProcedures = filteredProcedures.filter(
    (p) => !frequentlyUsedProcedureIds.includes(p.procedureId) && p.value
  );

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header
        closeButton
        style={{ backgroundColor: "#004aad", color: "white", borderBottom: "none" }}
      >
        <Modal.Title style={{ fontSize: isMobile ? '12px' : '24px' }}>
          Select Procedure
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "0" }}>
        <div style={{ padding: "1rem", borderBottom: "1px solid #dee2e6" }}>
          <Form.Control
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ fontSize: isMobile ? '10px' : '16px' }}
          />
        </div>

        <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "1rem" }}>
          {frequentlyUsed.length > 0 && (
            <>
              <h5 style={{ fontSize: isMobile ? '11px' : '20px' }}>Frequently Used</h5>

              <ListGroup variant="flush">
                {frequentlyUsed.map((proc) => (
                  <ListGroup.Item
                    key={`freq-${proc.value}`}
                    action
                    onClick={() => onSelect(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                      fontSize: isMobile ? '10px' : '16px'
                    }}
                  >
                    {proc.label}
                    {hasPricingAccess && ` (${formatAmount(proc.price || 0)})`}
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <hr />
            </>
          )}

          {generalProcedures.length > 0 && (
            <>
              <h5 style={{ fontSize: isMobile ? '11px' : '20px' }}>General Procedures</h5>

              <ListGroup variant="flush">
                {generalProcedures.map((proc) => (
                  <ListGroup.Item
                    key={`gen-${proc.value}`}
                    action
                    onClick={() => onSelect(proc.value)}
                    style={{
                      cursor: "pointer",
                      border: "none",
                      paddingLeft: "0",
                      fontSize: isMobile ? '10px' : '16px'
                    }}
                  >
                    {proc.label}
                    {hasPricingAccess && ` (${formatAmount(proc.price || 0)})`}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </>
          )}

          {frequentlyUsed.length === 0 && generalProcedures.length === 0 && (
            <p style={{ fontSize: isMobile ? '10px' : '16px' }}>
              No procedures available for this account.
            </p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

const ToothSelectorModal = ({ show, handleClose, currentTeeth, onSave }) => {
  const [selectedTeeth, setSelectedTeeth] = useState([]);
  const isMobile = useMediaQuery('(max-width: 767px)');

  useEffect(() => {
    if (currentTeeth) {
      setSelectedTeeth(
        currentTeeth
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t)
      );
    }
  }, [currentTeeth]);

  const toggleTooth = (toothNumber) => {
    setSelectedTeeth((prevSelected) =>
      prevSelected.includes(toothNumber.toString())
        ? prevSelected.filter((t) => t !== toothNumber.toString())
        : [...prevSelected, toothNumber.toString()]
    );
  };

  const toggleGroup = (teethArray) => {
    const teeth = teethArray.map((tooth) => tooth.toString());

    setSelectedTeeth((prevSelected) => {
      const allSelected = teeth.every((tooth) => prevSelected.includes(tooth));

      if (allSelected) {
        return prevSelected.filter((t) => !teeth.includes(t));
      }

      const newSelection = [...prevSelected];

      teeth.forEach((tooth) => {
        if (!newSelection.includes(tooth)) {
          newSelection.push(tooth);
        }
      });

      return newSelection;
    });
  };

  const handleDone = () => {
    onSave(
      [...selectedTeeth]
        .sort((a, b) => parseInt(a) - parseInt(b))
        .join(", ")
    );

    handleClose();
  };

  const renderToothButton = (toothNumber) => (
    <button
      key={toothNumber}
      onClick={() => toggleTooth(toothNumber)}
      className="tooth-button"
      style={
        selectedTeeth.includes(toothNumber.toString())
          ? selectedToothStyle
          : toothButtonStyle
      }
    >
      {toothNumber}
    </button>
  );

  const teethLayout = {
    upperRight: [18, 17, 16, 15, 14, 13, 12, 11],
    upperLeft: [21, 22, 23, 24, 25, 26, 27, 28],
    lowerRight: [48, 47, 46, 45, 44, 43, 42, 41],
    lowerLeft: [31, 32, 33, 34, 35, 36, 37, 38],
  };

  const thStyle = { cursor: "pointer", fontSize: isMobile ? '8px' : '16px' };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton style={{ backgroundColor: "#004aad", color: "white" }}>
        <Modal.Title style={{ fontSize: isMobile ? '12px' : '24px' }}>
          Tooth Selector
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={modalBodyStyle}>
        <div className="text-center mb-3">
          <h5
            style={{
              cursor: "pointer",
              display: "inline-block",
              fontSize: isMobile ? '11px' : '20px'
            }}
            onClick={() =>
              toggleGroup([...teethLayout.upperRight, ...teethLayout.upperLeft])
            }
          >
            Upper Arch
          </h5>
        </div>

        <div className="mb-4">
          <table className="table table-bordered text-center mb-2">
            <thead>
              <tr>
                <th colSpan="8" className="bg-light" style={thStyle}
                  onClick={() => toggleGroup(teethLayout.upperRight)}>
                  Upper Right
                </th>
                <th colSpan="8" className="bg-light" style={thStyle}
                  onClick={() => toggleGroup(teethLayout.upperLeft)}>
                  Upper Left
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                {teethLayout.upperRight.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>{renderToothButton(tooth)}</td>
                ))}
                {teethLayout.upperLeft.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>{renderToothButton(tooth)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <hr />

        <div className="text-center mb-3">
          <h5
            style={{
              cursor: "pointer",
              display: "inline-block",
              fontSize: isMobile ? '11px' : '20px'
            }}
            onClick={() =>
              toggleGroup([...teethLayout.lowerRight, ...teethLayout.lowerLeft])
            }
          >
            Lower Arch
          </h5>
        </div>

        <div>
          <table className="table table-bordered text-center mb-2">
            <thead>
              <tr>
                <th colSpan="8" className="bg-light" style={thStyle}
                  onClick={() => toggleGroup(teethLayout.lowerRight)}>
                  Lower Right
                </th>
                <th colSpan="8" className="bg-light" style={thStyle}
                  onClick={() => toggleGroup(teethLayout.lowerLeft)}>
                  Lower Left
                </th>
              </tr>
            </thead>

            <tbody>
              <tr>
                {teethLayout.lowerRight.map((tooth) => (
                  <td key={tooth} style={{ padding: "2px" }}>{renderToothButton(tooth)}</td>
                ))}
                {teethLayout.lowerLeft.map((tooth) => (
                  <td style={{ padding: "2px" }} key={tooth}>{renderToothButton(tooth)}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          style={{ fontSize: isMobile ? '11px' : '16px' }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleDone}
          style={{
            backgroundColor: "#004aad",
            borderColor: "#004aad",
            color: "white",
            fontSize: isMobile ? '11px' : '16px'
          }}
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const CustomDropdown = ({
  value,
  options,
  onSelect,
  placeholder = "Select...",
  disabled = false,
  className = "form-select",
  style = {},
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = React.useRef(null);

  const selected = options.find((opt) => opt.value === value);

  useEffect(() => {
    if (!open) return;

    const handleOutsideClick = (e) => {
      if (
        e.target.closest("[data-custom-select-menu]") ||
        e.target.closest("[data-custom-select-trigger]")
      ) {
        return;
      }
      setOpen(false);
    };

    const handleReposition = () => setOpen(false);

    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [open]);

  const toggle = () => {
    if (disabled) return;

    if (open) {
      setOpen(false);
      return;
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const menuHeightEstimate = Math.min(240, options.length * 36 + 8);

    let top = rect.bottom + 4;

    if (top + menuHeightEstimate > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - menuHeightEstimate - 4);
    }

    let left = rect.left;
    if (left + rect.width > window.innerWidth - margin) {
      left = window.innerWidth - rect.width - margin;
    }
    left = Math.max(margin, left);

    setPos({ top, left, width: rect.width });
    setOpen(true);
  };

  return (
    <>
      <div
        data-custom-select-trigger
        ref={triggerRef}
        className={className}
        onClick={toggle}
        style={{
          ...style,
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: disabled ? "#e9ecef" : (style.backgroundColor || "#fff"),
          userSelect: "none",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {selected ? selected.label : placeholder}
      </div>

      {open &&
        ReactDOM.createPortal(
          <div
            data-custom-select-menu
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              maxHeight: "240px",
              overflowY: "auto",
              backgroundColor: "white",
              border: "1px solid #ced4da",
              borderRadius: "6px",
              boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
              zIndex: 3000,
            }}
          >
            {options.map((opt) => (
              <div
                key={opt.value}
                onClick={() => {
                  onSelect(opt.value);
                  setOpen(false);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  fontSize: style.fontSize || "14px",
                  backgroundColor: opt.value === value ? "#f1f5ff" : "transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f1f1f1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    opt.value === value ? "#f1f5ff" : "transparent";
                }}
              >
                {opt.label}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

const EMPTY_PROCEDURE_OPTION = {
  label: "Select a procedure...",
  value: "",
  price: 0,
  procedureId: "",
  accountId: ""
};

const PROCEDURE_CACHE_TTL = 60 * 1000;
const procedureCache = new Map();

const EditProcedureModal = ({
  show,
  handleClose,
  procedure,
  caseId,
  account: accountProp,
  accountHolder,
  onProcedureUpdated,
  userLabId,
  hasPricingAccess = false,
}) => {
  const { formatAmount } = useCurrency();
  const isMobile = useMediaQuery('(max-width: 767px)');

  const formatAmountRef = useRef(formatAmount);
  formatAmountRef.current = formatAmount;

  const [editedProcedure, setEditedProcedure] = useState({
    procedure: {
      label: "Select Procedure",
      value: "",
      price: 0,
      procedureId: "",
      accountId: "",
    },
    teeth: "",
    shade: "",
    alloyMaterial: "",
    contactsEmbrasures: "",
    occlusalContact: "",
    insufficientRoom: "",
    retention: "",
    marginPosition: "",
    _id: "",
  });

  const [procedureOptions, setProcedureOptions] = useState([EMPTY_PROCEDURE_OPTION]);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [showProcedureSelectorModal, setShowProcedureSelectorModal] = useState(false);
  const [showToothSelectorModal, setShowToothSelectorModal] = useState(false);

  const caseAccountId = accountHolder ?? accountProp ?? null;

  useEffect(() => {
    if (!show) return undefined;

    if (!caseAccountId) {
      setProcedureOptions([EMPTY_PROCEDURE_OPTION]);
      setPricesLoading(false);
      return undefined;
    }

    if (!userLabId) {
      console.warn(
        "userLabId is missing, please ensure it's passed to EditProcedureModal"
      );

      setFetchError("User Lab ID is missing. Please refresh the page.");
      setPricesLoading(false);
      setProcedureOptions([EMPTY_PROCEDURE_OPTION]);
      return undefined;
    }

    const buildOptions = (list) => [
      EMPTY_PROCEDURE_OPTION,
      ...list.map((p) => ({
        label: hasPricingAccess
          ? `${p.name} (ID: ${p.procedureId}) - ${formatAmountRef.current(p.price)}`
          : `${p.name} (ID: ${p.procedureId})`,
        value: p._id,
        price: p.price,
        procedureId: p.procedureId,
        accountId: caseAccountId,
      })),
    ];

    const cacheKey = `${caseAccountId}|${userLabId}`;
    const cached = procedureCache.get(cacheKey);

    if (cached && Date.now() - cached.time < PROCEDURE_CACHE_TTL) {
      setFetchError(null);
      setProcedureOptions(buildOptions(cached.data));
      setPricesLoading(false);
      return undefined;
    }

    let cancelled = false;

    const fetchProcedures = async () => {
      try {
        setPricesLoading(true);
        setFetchError(null);

        const url =
          `https://hms.backend.hiragroupcloud.com/api/procedures/account-specific` +
          `?accountId=${caseAccountId}&userLabId=${userLabId}`;

        const { data } = await axios.get(url, { withCredentials: true });

        const list = Array.isArray(data.data) ? data.data : [];

        procedureCache.set(cacheKey, { data: list, time: Date.now() });

        if (!cancelled) {
          setProcedureOptions(buildOptions(list));
        }
      } catch (e) {
        console.error("Error fetching procedures:", e);

        if (!cancelled) {
          setFetchError(
            `Failed to load procedures: ${e.response?.data?.message || e.message}`
          );

          toast.error(
            `Failed to load procedures: ${e.response?.data?.message || e.message}`
          );

          setProcedureOptions([EMPTY_PROCEDURE_OPTION]);
        }
      } finally {
        if (!cancelled) {
          setPricesLoading(false);
        }
      }
    };

    fetchProcedures();

    return () => {
      cancelled = true;
    };
  }, [caseAccountId, show, userLabId, hasPricingAccess]);

  useEffect(() => {
    if (!procedure || !show || pricesLoading) return;

    const base = {
      procedure: {
        label: procedure.procedure || "Select Procedure",
        value: procedure.procedureId || "",
        price: procedure.price || 0,
        procedureId: procedure.procedureId || "",
        accountId: caseAccountId ?? "",
      },
      teeth: procedure.teeth || "",
      shade: procedure.shade || "",
      alloyMaterial: procedure.alloyMaterial || procedure.alley || "",
      contactsEmbrasures: procedure.contactsEmbrasures || procedure.contacts || "",
      occlusalContact: procedure.occlusalContact || procedure.occlusion || "",
      insufficientRoom: procedure.insufficientRoom || "",
      retention: procedure.retention || "",
      marginPosition: procedure.marginPosition || procedure.margin || "",
      _id: procedure._id || "",
    };

    setEditedProcedure(base);

    if (procedureOptions.length > 1) {
      const match = procedureOptions.find(
        (p) => p.value === procedure.procedureId
      );

      if (match) {
        setEditedProcedure((prev) => ({
          ...prev,
          procedure: {
            label: match.label,
            value: match.value,
            price: match.price,
            procedureId: match.procedureId,
            accountId: match.accountId,
          },
        }));
      }
    }
  }, [procedure, show, pricesLoading, procedureOptions, caseAccountId]);

  const handleProcedureSelect = (procedureId) => {
    const selected = procedureOptions.find((p) => p.value === procedureId);

    if (selected) {
      setEditedProcedure((prev) => ({
        ...prev,
        procedure: {
          label: selected.label,
          value: selected.value,
          price: selected.price,
          procedureId: selected.procedureId,
          accountId: selected.accountId,
        },
      }));
    }

    setShowProcedureSelectorModal(false);
  };

  const handleSaveTeeth = (teeth) => {
    setEditedProcedure((prev) => ({ ...prev, teeth }));
    setShowToothSelectorModal(false);
  };

  const handleChange = (name, value) => {
    setEditedProcedure((prev) => ({ ...prev, [name]: value }));
  };

  const onSave = async () => {
    const missing = ["procedure", "teeth", "shade"].filter((field) =>
      field === "procedure"
        ? !editedProcedure.procedure.value
        : !editedProcedure[field]?.trim()
    );

    if (missing.length) {
      toast.error(`Please fill in: ${missing.join(", ")}`);
      return;
    }

    const payload = {
      customProcedureId: editedProcedure.procedure.procedureId,
      teeth: editedProcedure.teeth,
      shade: editedProcedure.shade,
      alloyMaterial: editedProcedure.alloyMaterial,
      contactsEmbrasures: editedProcedure.contactsEmbrasures,
      occlusalContact: editedProcedure.occlusalContact,
      insufficientRoom: editedProcedure.insufficientRoom,
      retention: editedProcedure.retention,
      marginPosition: editedProcedure.marginPosition,
      price: editedProcedure.procedure.price,
      unitCount: null,
    };

    try {
      const url =
        `https://hms.backend.hiragroupcloud.com/api/cases/${caseId}` +
        `/procedures/${editedProcedure._id}`;

      const res = await axios.put(url, payload, { withCredentials: true });

      toast.success("Procedure updated successfully!");

      onProcedureUpdated?.(res.data.data);
      handleClose();
    } catch (err) {
      console.error("Update failed:", err);

      toast.error(err.response?.data?.message || "Failed to update procedure");
    }
  };

  const alloyOptions = ["", "Zirconia", "Emax", "PFM", "Gold", "Composite"];
  const contactOptions = ["", "Normal", "Broad", "Open", "Closed", "Light"];
  const occlusalOptions = ["", "In Occlusion", "Out of Occlusion", "Light Contact"];
  const roomOptions = ["", "Yes", "No", "Minimal Prep Required"];
  const retentionOptions = ["", "Good", "Fair", "Poor", "Mechanical", "Adhesive"];
  const marginOptions = ["", "Supragingival", "Equigingival", "Subgingival"];

  const fs16 = isMobile ? '10px' : '16px';

  const LABEL_PCT = "20%";
  const FIELD_PCT = "80%";

  const labelColStyle = isMobile
    ? { paddingLeft: 0 }
    : { flex: `0 0 ${LABEL_PCT}`, maxWidth: LABEL_PCT, paddingLeft: 0 };

  const fieldColStyle = isMobile
    ? undefined
    : { flex: `0 0 ${FIELD_PCT}`, maxWidth: FIELD_PCT, paddingRight: 0 };

  const fieldBoxStyle = {
    borderColor: "#f4f4f4",
    boxShadow: "0 0 5px rgba(0,74,173,0.3)",
    borderRadius: "8px",
    fontSize: fs16,
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton style={{ backgroundColor: "#004aad", color: "white" }}>
        <Modal.Title style={{ fontSize: isMobile ? '12px' : '24px' }}>
          Edit Procedure
        </Modal.Title>
      </Modal.Header>

      <Modal.Body
        style={{
          overflowY: "auto",
          height: isMobile ? "auto" : "min(600px, calc(100vh - 200px))",
          minHeight: isMobile ? undefined : "420px",
          maxHeight: isMobile ? "70vh" : "none",
        }}
      >
        <Form>
          <Form.Group className="mb-3" controlId="formProcedure">
            <Form.Label style={{ ...requiredLabelStyle, fontSize: fs16 }}>
              Procedure{" "}
              <span style={{ color: "red" }}>*</span>
            </Form.Label>

            {pricesLoading ? (
              <div className="text-muted" style={{ fontSize: fs16 }}>
                Loading procedures...
              </div>
            ) : fetchError ? (
              <div className="text-danger" style={{ fontSize: fs16 }}>
                {fetchError}
              </div>
            ) : procedureOptions.length <= 1 ? (
              <div className="text-muted" style={{ fontSize: fs16 }}>
                No procedures for account:{" "}
                <strong>{caseAccountId || "N/A"}</strong>
              </div>
            ) : (
              <InputGroup
                style={{
                  width: isMobile ? "100%" : FIELD_PCT,
                  marginLeft: isMobile ? "0" : LABEL_PCT,
                  flexWrap: "nowrap",
                }}
              >
                <CustomDropdown
                  value={editedProcedure.procedure.value}
                  onSelect={(val) => handleProcedureSelect(val)}
                  options={procedureOptions.map((opt) => ({
                    value: opt.value,
                    label: opt.label.replace(/\(ID:.*\)/, "").trim(),
                  }))}
                  style={{
                    ...fieldBoxStyle,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    minWidth: 0,
                  }}
                />

                <Button
                  variant="outline-secondary"
                  onClick={() => setShowProcedureSelectorModal(true)}
                  style={{
                    backgroundColor: "#004aad",
                    borderColor: "#004aad",
                    color: "#fff",
                    fontSize: fs16,
                    flexShrink: 0,
                    borderRadius: "0 8px 8px 0",
                  }}
                  disabled={procedureOptions.length <= 1}
                >
                  <FaRegCopy style={{ fontSize: fs16 }} />
                </Button>
              </InputGroup>
            )}
          </Form.Group>

          {editedProcedure.procedure.value && (
            <>
              <Form.Group as={Row} className="mb-3 mx-0" controlId="formTeeth">
                <Form.Label
                  column
                  sm={2}
                  style={{ ...requiredLabelStyle, fontSize: fs16, ...labelColStyle }}
                >
                  Teeth{" "}
                  <span style={{ color: "red" }}>*</span>
                </Form.Label>

                <Col sm={10} style={fieldColStyle}>
                  <InputGroup style={{ flexWrap: "nowrap" }}>
                    <Form.Control
                      type="text"
                      value={editedProcedure.teeth}
                      readOnly
                      onClick={() => setShowToothSelectorModal(true)}
                      placeholder="Select Teeth"
                      style={{
                        cursor: "pointer",
                        borderColor: "#f4f4f4",
                        boxShadow: "0 0 5px rgba(0,74,173,0.3)",
                        borderRadius: "8px 0 0 8px",
                        fontSize: fs16
                      }}
                    />

                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowToothSelectorModal(true)}
                      style={{
                        backgroundColor: "#004aad",
                        borderColor: "#004aad",
                        borderRadius: "0 8px 8px 0",
                        fontSize: fs16,
                        flexShrink: 0,
                      }}
                    >
                      <FaTooth style={{ color: "white", fontSize: fs16 }} />
                    </Button>
                  </InputGroup>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3 mx-0" controlId="formShade">
                <Form.Label
                  column
                  sm={2}
                  style={{ ...requiredLabelStyle, fontSize: fs16, ...labelColStyle }}
                >
                  Shade{" "}
                  <span style={{ color: "red" }}>*</span>
                </Form.Label>

                <Col sm={10} style={fieldColStyle}>
                  <Form.Control
                    type="text"
                    value={editedProcedure.shade}
                    onChange={(e) => handleChange("shade", e.target.value)}
                    placeholder="Enter shade"
                    style={{
                      borderColor: "#f4f4f4",
                      boxShadow: "0 0 5px rgba(0,74,173,0.3)",
                      borderRadius: "8px",
                      fontSize: fs16
                    }}
                  />
                </Col>
              </Form.Group>

              {[
                { label: "Alloy / Material", name: "alloyMaterial", options: alloyOptions },
                { label: "Contacts / Embrasures", name: "contactsEmbrasures", options: contactOptions },
                { label: "Occlusal Contact", name: "occlusalContact", options: occlusalOptions },
                { label: "Insufficient Room", name: "insufficientRoom", options: roomOptions },
                { label: "Retention", name: "retention", options: retentionOptions },
                { label: "Margin Position", name: "marginPosition", options: marginOptions },
              ].map((field) => (
                <Form.Group
                  as={Row}
                  className="mb-3 mx-0"
                  controlId={`form${field.name}`}
                  key={field.name}
                >
                  <Form.Label
                    column
                    sm={2}
                    style={{
                      fontWeight: 600,
                      color: "#333",
                      fontSize: fs16,
                      ...labelColStyle
                    }}
                  >
                    {field.label}
                  </Form.Label>

                  <Col sm={10} style={fieldColStyle}>
                    <CustomDropdown
                      value={editedProcedure[field.name]}
                      onSelect={(val) => handleChange(field.name, val)}
                      options={field.options.map((opt) => ({
                        value: opt,
                        label: opt || "—",
                      }))}
                      style={fieldBoxStyle}
                    />
                  </Col>
                </Form.Group>
              ))}
            </>
          )}
        </Form>
      </Modal.Body>

      <Modal.Footer style={{ backgroundColor: "#f8f9fa" }}>
        <Button
          variant="secondary"
          onClick={handleClose}
          style={{ fontSize: isMobile ? '11px' : '16px' }}
        >
          Cancel
        </Button>

        <Button
          variant="primary"
          onClick={onSave}
          style={{
            backgroundColor: "#004aad",
            borderColor: "#004aad",
            fontSize: isMobile ? '11px' : '16px'
          }}
          disabled={
            pricesLoading ||
            !editedProcedure.procedure.value ||
            !editedProcedure.teeth ||
            !editedProcedure.shade
          }
        >
          Save Changes
        </Button>
      </Modal.Footer>

      <ProcedureSelectorModal
        show={showProcedureSelectorModal}
        handleClose={() => setShowProcedureSelectorModal(false)}
        onSelect={handleProcedureSelect}
        allProcedures={procedureOptions}
        accountId={caseAccountId}
        hasPricingAccess={hasPricingAccess}
      />

      <ToothSelectorModal
        show={showToothSelectorModal}
        handleClose={() => setShowToothSelectorModal(false)}
        currentTeeth={editedProcedure.teeth}
        onSave={handleSaveTeeth}
      />
    </Modal>
  );
};

const RenameFileModal = ({ show, file, onClose, onSave }) => {
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [baseName, setBaseName] = useState('');
  const [saving, setSaving] = useState(false);

  const fullName =
    file?.name || (file?.path ? file.path.split(/[\\/]/).pop() : '') || '';
  const dotIndex = fullName.lastIndexOf('.');
  const ext = dotIndex > 0 ? fullName.slice(dotIndex) : '';

  useEffect(() => {
    if (show && file) {
      setBaseName(ext ? fullName.slice(0, -ext.length) : fullName);
      setSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, file]);

  const handleSubmit = async () => {
    const trimmed = baseName.trim();

    if (!trimmed) {
      toast.error('File name cannot be empty');
      return;
    }

    if (/[\\/:*?"<>|]/.test(trimmed)) {
      toast.error('File name cannot contain  \\ / : * ? " < > |');
      return;
    }

    if (trimmed + ext === fullName) {
      onClose();
      return;
    }

    setSaving(true);
    const ok = await onSave(file._id, trimmed + ext);
    setSaving(false);

    if (ok) onClose();
  };

  return (
    <Modal show={show} onHide={saving ? undefined : onClose} centered>
      <Modal.Header
        closeButton={!saving}
        style={{ backgroundColor: "#004aad", color: "white", borderBottom: "none" }}
      >
        <Modal.Title style={{ fontSize: isMobile ? '12px' : '22px' }}>
          Rename File
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Label
          style={{ fontWeight: 600, fontSize: isMobile ? '10px' : '15px' }}
        >
          File name
        </Form.Label>

        <InputGroup>
          <Form.Control
            type="text"
            value={baseName}
            autoFocus
            maxLength={200}
            disabled={saving}
            onChange={(e) => setBaseName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder="Enter new file name"
            style={{ fontSize: isMobile ? '11px' : '16px' }}
          />

          {ext && (
            <InputGroup.Text style={{ fontSize: isMobile ? '11px' : '16px' }}>
              {ext}
            </InputGroup.Text>
          )}
        </InputGroup>

        <div
          className="text-muted mt-2"
          style={{ fontSize: isMobile ? '9px' : '13px' }}
        >
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onClose}
          disabled={saving}
          style={{ fontSize: isMobile ? '11px' : '16px' }}
        >
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={saving || !baseName.trim()}
          style={{
            backgroundColor: "#004aad",
            borderColor: "#004aad",
            color: "white",
            fontSize: isMobile ? '11px' : '16px'
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const tableStyles = `
  .procedure-table {
    width: 100%;
    border-collapse: collapse !important;
    border-left: 2px solid #dee2e6 !important;
    border-right: 2px solid #dee2e6 !important;
    border-bottom: 2px solid #dee2e6 !important;
  }

  .procedure-table {
    border-top: none !important;
  }

  .procedure-table thead th {
    background-color: #f1f1f1 !important;
    color: #000 !important;
    border: none !important;
    padding: 12px;
    font-weight: 600;
  }

  .procedure-table thead tr:hover th,
  .procedure-table thead th:hover {
    background-color: #f1f1f1 !important;
    color: #000 !important;
  }

  .procedure-table tbody td {
    background-color: #fff;
    border: none !important;
    padding: 12px;
    transition: background-color 0.2s ease;
  }

  .procedure-table tbody tr:hover td {
    background-color: #f1f1f1 !important;
  }

  .procedure-table tbody tr:hover button {
    background-color: #004aad !important;
    color: #fff !important;
  }

  .procedure-table tbody tr:hover svg {
    background: transparent !important;
  }

  .procedure-table tbody tr[style] td {
    background-color: #f8f9fa !important;
  }

  .procedure-table tbody tr {
    border-bottom: 1px solid #dee2e6 !important;
  }

  .procedure-table tbody tr:last-child {
    border-bottom: none !important;
  }

  @media (max-width: 767px) {

    .procedure-table-wrap {
      overflow-x: visible !important;
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    .procedure-table-wrap > .procedure-table {
      padding-left: 0 !important;
      padding-right: 0 !important;
      table-layout: fixed !important;
      width: 100% !important;
      max-width: 100% !important;
    }

    .procedure-table thead th,
    .procedure-table tbody td {
      padding: 5px 3px !important;
      font-size: 9px !important;
      white-space: normal !important;
      word-break: break-word !important;
      overflow-wrap: anywhere !important;
    }

    .procedure-table thead th {
      font-size: 9px !important;
    }

    .procedure-table .pt-col-units {
      width: 9% !important;
      min-width: 0 !important;
    }

    .procedure-table .pt-col-price {
      width: 21% !important;
      min-width: 0 !important;
    }

    .procedure-table .pt-col-actions {
      width: 66px !important;
      min-width: 0 !important;
    }

    .procedure-table .procedure-units-cell {
      padding-left: 3px !important;
      padding-right: 2px !important;
    }

    .procedure-table tbody td button {
      white-space: nowrap !important;
    }
  }

  @media (max-width: 767px) {

    .work-requested-header {
      display: flex !important;
      flex-wrap: nowrap !important;
      align-items: center !important;
      justify-content: space-between !important;
      width: 100% !important;
      min-width: 0 !important;
      overflow: hidden !important;
      padding: 3px 5px !important;
      margin: 0 !important;
      margin-top: 5px !important;
      border-radius: 5px !important;
      box-sizing: border-box !important;
    }

    .work-requested-title-container {
      flex: 1 1 auto !important;
      min-width: 0 !important;
      width: auto !important;
      display: flex !important;
      align-items: center !important;
      overflow: hidden !important;
    }

    .work-requested-title {
      margin: 0 !important;
      padding: 0 !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      font-size: 11px !important;
      line-height: 1 !important;
      font-weight: 600 !important;
    }

    .work-requested-button-container {
      flex: 0 0 auto !important;
      width: auto !important;
      max-width: none !important;
      display: flex !important;
      align-items: center !important;
      justify-content: flex-end !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: visible !important;
    }

    .work-requested-procedure-wrapper {
      display: flex !important;
      align-items: center !important;
      justify-content: flex-end !important;
      margin: 0 !important;
      padding: 0 !important;
      transform: none !important;
      transform-origin: center !important;
      white-space: nowrap !important;
    }

    .work-requested-procedure-wrapper button {
      font-size: 8px !important;
      line-height: 1 !important;
      padding: 2px 5px !important;
      min-height: 20px !important;
      height: 20px !important;
      margin: 0 !important;
    }

    .work-requested-procedure-wrapper button svg {
      font-size: 7px !important;
      width: 7px !important;
      height: 7px !important;
      margin-left: 2px !important;
    }

    .work-requested-procedure-wrapper > div,
    .work-requested-procedure-wrapper > span {
      padding: 0 !important;
      margin: 0 !important;
    }
  }

  @media (max-width: 400px) {

    .work-requested-header {
      padding: 2px 4px !important;
      margin-top: 4px !important;
      border-radius: 4px !important;
    }

    .work-requested-title {
      font-size: 10px !important;
      line-height: 1 !important;
    }

    .work-requested-procedure-wrapper button {
      font-size: 7px !important;
      padding: 1px 4px !important;
      height: 18px !important;
      min-height: 18px !important;
    }

    .work-requested-procedure-wrapper button svg {
      font-size: 6px !important;
      width: 6px !important;
      height: 6px !important;
    }
  }

  @media (max-width: 320px) {

    .work-requested-header {
      padding: 2px 3px !important;
    }

    .work-requested-title {
      font-size: 9px !important;
    }

    .work-requested-procedure-wrapper button {
      font-size: 6px !important;
      padding: 1px 3px !important;
      height: 17px !important;
      min-height: 17px !important;
    }

    .work-requested-procedure-wrapper button svg {
      font-size: 5px !important;
      width: 5px !important;
      height: 5px !important;
    }
  }
`;

const ALL_STYLES = tableStyles + uploadStyles;

const displayValue = (value, defaultValue = '') => {
  return value && value !== defaultValue ? value : '-';
};

const ProcedureRow = React.memo(function ProcedureRow({
  proc,
  procId,
  expanded,
  isMobile,
  hasPricingAccess,
  priceText,
  menuPos,
  deleting,
  onToggleExpand,
  onEdit,
  onOpenMenu,
  onDeleteProcedure,
}) {
  const g = (d, m) => rf(isMobile, d, m);

  return (
    <>
      <tr className="align-middle">
        <td
          className="procedure-units-cell"
          style={{
            fontWeight: "normal",
            fontSize: g("16px", "10px"),
            whiteSpace: "nowrap",
            paddingLeft: g("8px", "14px"),
            paddingRight: "4px",
          }}
        >
          {proc.calculatedUnits}
        </td>

        <td
          style={{
            fontSize: g("18px", "10px"),
            fontWeight: "normal",
            paddingLeft: "6px",
            paddingRight: "6px",
          }}
        >
          <span style={{ display: "inline" }}>{proc.procedure}</span>

          <IoIosArrowDropdownCircle
            onClick={() => onToggleExpand(procId)}
            style={{
              cursor: "pointer",
              marginLeft: g("15px", "8px"),
              fontSize: g("1.2em", "1em"),
              minWidth: g("19px", "16px"),
              minHeight: g("19px", "16px"),
              verticalAlign: "middle",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in-out",
              color: "#004aad",
              flexShrink: 0,
            }}
            title={expanded ? "Collapse" : "Expand"}
          />
        </td>

        {hasPricingAccess && (
          <td
            style={{
              textAlign: "right",
              fontSize: g("16px", "10px"),
              whiteSpace: "nowrap",
              paddingLeft: "4px",
              paddingRight: "6px",
            }}
          >
            {priceText}
          </td>
        )}

        <td
          style={{
            padding: "4px",
            whiteSpace: "nowrap",
            minWidth: g("75px", "65px"),
          }}
        >
          <div
            style={{
              position: "relative",
              display: "inline-flex",
              alignItems: "stretch",
              whiteSpace: "nowrap",
            }}
          >
            <button
              onClick={() => onEdit(proc)}
              style={{
                backgroundColor: "#004aad",
                color: "white",
                border: "none",
                borderRadius: "5px 0 0 5px",
                padding: g("5px 12px", "4px 7px"),
                fontSize: g("14px", "9px"),
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: g("32px", "24px"),
                minWidth: g("45px", "38px"),
              }}
            >
              Edit
            </button>

            <button
              data-case-row-toggle
              onClick={(e) => {
                e.stopPropagation();
                onOpenMenu(procId, e.currentTarget);
              }}
              style={{
                backgroundColor: "#004aad",
                color: "white",
                border: "none",
                borderLeft: "1px solid rgba(255,255,255,0.35)",
                borderRadius: "0 5px 5px 0",
                padding: g("5px 8px", "4px 6px"),
                fontSize: g("12px", "9px"),
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                height: g("32px", "24px"),
                minWidth: g("25px", "22px"),
                flexShrink: 0,
              }}
              aria-label="Open procedure actions"
            >
              ▼
            </button>

            {menuPos &&
              ReactDOM.createPortal(
                <div
                  data-case-row-menu
                  style={{
                    position: "fixed",
                    top: menuPos.top,
                    left: menuPos.left,
                    backgroundColor: "white",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 2000,
                    minWidth: g("120px", "100px"),
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => onDeleteProcedure(proc._id)}
                    disabled={deleting}
                    style={{
                      width: "100%",
                      padding: g("8px 16px", "6px 10px"),
                      border: "none",
                      backgroundColor: "transparent",
                      color: "#dc3545",
                      cursor: deleting ? "not-allowed" : "pointer",
                      textAlign: "left",
                      fontSize: g("14px", "9px"),
                      display: "flex",
                      alignItems: "center",
                      gap: g("8px", "5px"),
                      transition: "background-color 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }}
                  >
                    <FaTrash size={g("14px", "9px")} />

                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                </div>,
                document.body
              )}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr style={{ backgroundColor: "#f8f9fa" }}>
          <td colSpan={hasPricingAccess ? 4 : 3}>
            <div style={{ padding: g("15px", "8px") }}>
              <strong style={{ fontSize: g("16px", "10px") }}>
                Full Details:
              </strong>

              <div className="row mt-2">
                <div className="col-md-6">
                  <p style={{ fontSize: g("14px", "9px") }}>
                    <strong>Teeth:</strong>{" "}
                    {displayValue(proc.teeth || proc.teethCount)}
                  </p>

                  <p style={{ fontSize: g("14px", "9px") }}>
                    <strong>Shade:</strong>{" "}
                    {displayValue(proc.shade)}
                  </p>

                  {hasPricingAccess && (
                    <p style={{ fontSize: g("14px", "9px") }}>
                      <strong>Price:</strong>{" "}
                      {priceText}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
});

const FileCard = React.memo(function FileCard({
  file,
  variant,
  isMobile,
  onDownload,
  onView,
  onDelete,
  onReplace,
  onRename,
  onTogglePrivacy,
  selected = false,
  onToggleSelect,
  selectable = false,
}) {
  const g = (d, m) => rf(isMobile, d, m);

  const isPly = variant === 'other' && isPlyFile(file);

  const iconBtn = (color, big) => ({
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: big ? g('16px', '10px') : g('14px', '8px'),
    color,
    padding: g('5px 3px', '2px')
  });

  const handleReplaceClick = () => {
    const input = document.createElement('input');

    input.type = 'file';
    input.accept = REPLACE_ACCEPT[variant];

    input.onchange = (event) => {
      const newFile = event.target.files?.[0];

      if (newFile) {
        onReplace(file._id, newFile, variant);
      }
    };

    input.click();
  };

  const labelStyle = {
    fontSize: g('16px', '10px'),
    fontWeight: '700'
  };

  const formatDateTime = (value) => {
    if (!value) return 'Unknown';

    try {
      const d = new Date(value);

      if (isNaN(d.getTime())) return 'Unknown';

      return d.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Unknown';
    }
  };

  const uploadedAt = formatDateTime(
    file?.date || file?.createdAt || file?.uploadedAt
  );

  const updatedAt = formatDateTime(
    file?.updatedAt || file?.date || file?.createdAt
  );

  return (
    <div
      style={{
        width: g('140px', '100px'),
        minHeight: g('300px', '200px'),
        borderRadius: '8px',
        border: selected ? '2px solid #004aad' : '1px solid #ccc',
        boxShadow: selected
          ? '0 0 0 2px rgba(0,74,173,0.25), 2px 2px 10px rgba(0,0,0,0.1)'
          : '2px 2px 10px rgba(0,0,0,0.1)',
        padding: g('5px', '3px'),
        backgroundColor: selected ? '#f0f6ff' : 'white',
        textAlign: 'center',
        position: 'relative',
        cursor: 'pointer'
      }}
      onClick={() => onDownload(file)}
    >
      {selectable && (
        <div
          onClick={(e) => {
            e.stopPropagation();
            onToggleSelect?.(file._id);
          }}
          title={selected ? 'Deselect' : 'Select for multi-view'}
          style={{
            position: 'absolute',
            top: g('6px', '4px'),
            left: g('6px', '4px'),
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: g('22px', '18px'),
            height: g('22px', '18px'),
            borderRadius: '5px',
            background: selected ? '#004aad' : 'rgba(255,255,255,0.92)',
            border: selected ? '2px solid #004aad' : '2px solid #b8c4d6',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            transition: 'all 0.15s ease'
          }}
        >
          {selected && (
            <FaCheck style={{ color: '#fff', fontSize: g('11px', '9px') }} />
          )}
        </div>
      )}

      {variant === 'image' && (
        <img
          src={getImageUrl(file.path)}
          alt={file.name}
          loading="lazy"
          onError={handleImgError}
          style={{
            width: "100%",
            height: g('140px', '80px'),
            objectFit: "contain"
          }}
        />
      )}

      {variant === 'stl' && (
        <div
          style={{
            width: "100%",
            height: g('140px', '80px'),
            overflow: "hidden"
          }}
        >
          <LazySTLThumbnail
            fileUrl={getImageUrl(file.path)}
            cacheKey={`${file._id}-${file.updatedAt}`}
            height="100%"
            onViewClick={() => onView(file)}
          />
        </div>
      )}

      {variant === 'other' && (
        <div
          style={{
            height: g('140px', '80px'),
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {isPly ? (
            <LazySTLThumbnail
              fileUrl={getImageUrl(file.path)}
              cacheKey={`${file._id}-${file.updatedAt}`}
              height="100%"
              onViewClick={() => onView(file)}
            />
          ) : file.path.toLowerCase().endsWith('.pdf') ? (
            <FaFilePdf
              style={{
                fontSize: g('3rem', '1.8rem'),
                color: 'red'
              }}
            />
          ) : (
            <FaFile style={{ fontSize: g('3rem', '1.8rem') }} />
          )}
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: g('4px', '2px'),
          marginTop: '5px',
          borderBottom: '2px solid #ddd',
          paddingBottom: '8px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {(variant !== 'other' || isPly) && (
          <button
            onClick={() => onView(file)}
            title={variant === 'other' ? "View 3D model" : undefined}
            style={iconBtn("#007bff", true)}
          >
            <PiImage />
          </button>
        )}

        <button
          onClick={() => onDownload(file)}
          style={iconBtn("#28a745", false)}
          title="Download file"
        >
          <FaDownload />
        </button>

        <button
          onClick={() => onDelete(file._id)}
          style={iconBtn("red", false)}
        >
          <FaTrash />
        </button>

        <button
          onClick={handleReplaceClick}
          style={iconBtn("orange", false)}
          title="Replace file"
        >
          <FaSync />
        </button>

        <button
          onClick={() => onRename(file)}
          style={iconBtn("#6f42c1", false)}
          title="Rename file"
        >
          <FaPen />
        </button>
      </div>

      <div
        style={{
          textAlign: "left",
          padding: "5px",
          fontSize: g('13px', '8px'),
          lineHeight: "1.2",
          wordBreak: "break-word"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ marginBottom: "4px" }}>
          <strong style={labelStyle}>Updated At:</strong>{" "}
          {updatedAt}
        </div>

        <div style={{ marginBottom: "4px" }}>
          <strong style={labelStyle}>File:</strong>{" "}
          {variant === 'image'
            ? file.name || file.path.split("/").pop()
            : file.name}
        </div>

        <div style={{ marginBottom: "4px" }}>
          <strong style={labelStyle}>From:</strong>{" "}
          Lab
        </div>

        <div style={{ marginBottom: "6px" }}>
          <strong style={labelStyle}>Uploaded By:</strong>{" "}
          {file.uploadedBy || 'Unknown User'}
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <input
            type="checkbox"
            checked={file.private || false}
            onChange={() => onTogglePrivacy(file._id, file.private)}
          />

          Private
        </label>
      </div>
    </div>
  );
});

const getInitialLabId = () => {
  try {
    const storedLabId = localStorage.getItem('userLabId');
    if (storedLabId) return storedLabId;

    const sessionLabId = sessionStorage.getItem('userLabId');
    if (sessionLabId) return sessionLabId;

    if (window.userLabId) return window.userLabId;
  } catch (e) {
    // ignore storage errors
  }

  return null;
};

const getUploaderName = () => {
  try {
    const name =
      localStorage.getItem('userName') ||
      sessionStorage.getItem('userName') ||
      '';
    return name.trim();
  } catch {
    return '';
  }
};

/* ============================================================
   DOWNLOAD MANAGER HOOK
   - Handles streaming download with progress
   - Deduplicates: if file is already queued/downloading, skip
   - Retry support
   - Proper blob lifecycle (wait before revoking)
   - concurrency limit to avoid browser throttling
   ============================================================ */
const useDownloadManager = () => {
  const [items, setItems] = useState([]);
  const [startedAt, setStartedAt] = useState(Date.now());
  const [running, setRunning] = useState(false);

  // Refs hold live state so the async runner doesn't rely on stale closures
  const itemsRef = useRef([]);
  const abortMapRef = useRef(new Map());
  const queueRef = useRef([]);
  const activeCountRef = useRef(0);
  const startedAtRef = useRef(Date.now());
  const runningRef = useRef(false);
  const cancelledRef = useRef(false);

  const CONCURRENCY = 2;
  const MAX_RETRIES = 2;

  const syncItems = useCallback((updater) => {
    setItems((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      itemsRef.current = next;
      return next;
    });
  }, []);

  const updateItem = useCallback((id, patch) => {
    syncItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, ...patch } : it))
    );
  }, [syncItems]);

  const triggerDownload = useCallback((blob, name) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name || `file_${Date.now()}`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    // Wait long enough for the browser to actually start the download.
    // Revoking too early (100ms) is the #1 cause of "sometimes it doesn't download".
    setTimeout(() => {
      try {
        document.body.removeChild(link);
      } catch (_) { /* ignore */ }
      window.URL.revokeObjectURL(url);
    }, 5000);
  }, []);

  // Runs a single item with retry
  const runItem = useCallback(async (job) => {
    const { id, url, name } = job;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
      if (cancelledRef.current) return;

      if (attempt > 0) {
        updateItem(id, { status: 'downloading', loaded: 0, error: null });
      }

      const controller = new AbortController();
      abortMapRef.current.set(id, controller);

      try {
        const res = await fetch(url, {
          credentials: 'include',
          signal: controller.signal,
          // helps large files stream properly
          headers: { Accept: '*/*' }
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status} ${res.statusText}`);
        }

        // Prefer content-length for total size
        const contentLength = res.headers.get('content-length');
        const totalSize = contentLength ? parseInt(contentLength, 10) : (job.size || 0);

        updateItem(id, { size: totalSize, loaded: 0, status: 'downloading' });

        if (!res.body || !res.body.getReader) {
          // Browser can't stream — fall back to blob (still tracked)
          const blob = await res.blob();
          updateItem(id, { loaded: blob.size, size: blob.size, status: 'saving' });
          triggerDownload(blob, name);
          updateItem(id, { status: 'done', loaded: blob.size });
          return;
        }

        const reader = res.body.getReader();
        const chunks = [];
        let loaded = 0;
        let lastUpdate = 0;

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();

          if (done) break;
          if (cancelledRef.current) {
            controller.abort();
            return;
          }

          chunks.push(value);
          loaded += value.length;

          const now = Date.now();
          if (now - lastUpdate > 120 || (totalSize && loaded === totalSize)) {
            lastUpdate = now;
            updateItem(id, {
              loaded,
              size: totalSize || loaded,
              status: 'downloading'
            });
          }
        }

        const blob = new Blob(chunks);
        updateItem(id, { loaded: blob.size, size: blob.size, status: 'saving' });

        triggerDownload(blob, name);
        updateItem(id, { status: 'done', loaded: blob.size });

        abortMapRef.current.delete(id);
        return; // success — exit retry loop
      } catch (err) {
        abortMapRef.current.delete(id);

        if (err.name === 'AbortError' || cancelledRef.current) {
          return;
        }

        if (attempt < MAX_RETRIES) {
          // wait a bit then retry
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => setTimeout(r, 600 * (attempt + 1)));
          continue;
        }

        updateItem(id, {
          status: 'error',
          error: err.message || 'Download failed'
        });
        return;
      }
    }
  }, [triggerDownload, updateItem]);

  const pumpQueue = useCallback(() => {
    while (activeCountRef.current < CONCURRENCY && queueRef.current.length > 0) {
      const job = queueRef.current.shift();
      activeCountRef.current += 1;

      runItem(job).finally(() => {
        activeCountRef.current -= 1;
        if (queueRef.current.length > 0) {
          pumpQueue();
        } else if (activeCountRef.current === 0) {
          runningRef.current = false;
          setRunning(false);
        }
      });
    }
  }, [runItem]);

  /**
   * Start a download batch.
   * @param {Array<{file: object, url: string}>} jobs
   */
  const startDownloads = useCallback((jobs) => {
    if (!jobs || jobs.length === 0) return;

    // dedupe within this batch
    const seen = new Set();
    const dedupedJobs = [];
    jobs.forEach(({ file, url }) => {
      if (seen.has(file._id)) return;
      seen.add(file._id);
      dedupedJobs.push({ file, url });
    });

    // filter out anything already in-progress or queued
    const inFlight = new Set(
      itemsRef.current
        .filter((it) => it.status === 'queued' || it.status === 'downloading' || it.status === 'saving')
        .map((it) => it.fileId)
    );

    const freshJobs = dedupedJobs.filter(({ file }) => !inFlight.has(file._id));

    if (freshJobs.length === 0) {
      toast.info('These files are already being downloaded');
      return;
    }

    cancelledRef.current = false;

    if (!runningRef.current) {
      startedAtRef.current = Date.now();
      setStartedAt(startedAtRef.current);
      runningRef.current = true;
      setRunning(true);
    }

    const newItems = freshJobs.map(({ file, url }) => ({
      id: `${file._id}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      fileId: file._id,
      name: file.name || (file.path ? file.path.split(/[\\/]/).pop() : 'file'),
      url,
      size: 0,
      loaded: 0,
      status: 'queued',
      error: null
    }));

    syncItems((prev) => [...prev, ...newItems]);

    queueRef.current.push(
      ...newItems.map((it) => ({ id: it.id, url: it.url, name: it.name }))
    );

    pumpQueue();
  }, [pumpQueue, syncItems]);

  const cancel = useCallback(() => {
    cancelledRef.current = true;

    abortMapRef.current.forEach((ctrl) => {
      try { ctrl.abort(); } catch (_) { /* ignore */ }
    });
    abortMapRef.current.clear();
    queueRef.current = [];

    syncItems((prev) =>
      prev.map((it) =>
        it.status === 'queued' || it.status === 'downloading' || it.status === 'saving'
          ? { ...it, status: 'canceled' }
          : it
      )
    );

    runningRef.current = false;
    setRunning(false);
  }, [syncItems]);

  const dismiss = useCallback(() => {
    syncItems([]);
    queueRef.current = [];
    abortMapRef.current.clear();
    setRunning(false);
    runningRef.current = false;
    cancelledRef.current = false;
  }, [syncItems]);

  const retry = useCallback((itemId) => {
    const it = itemsRef.current.find((x) => x.id === itemId);
    if (!it) return;

    cancelledRef.current = false;

    updateItem(itemId, { status: 'queued', loaded: 0, error: null, size: 0 });

    queueRef.current.push({ id: it.id, url: it.url, name: it.name });

    if (!runningRef.current) {
      startedAtRef.current = Date.now();
      setStartedAt(startedAtRef.current);
      runningRef.current = true;
      setRunning(true);
    }

    pumpQueue();
  }, [pumpQueue, updateItem]);

  // cleanup on unmount
  useEffect(() => () => {
    abortMapRef.current.forEach((ctrl) => {
      try { ctrl.abort(); } catch (_) { /* ignore */ }
    });
    abortMapRef.current.clear();
    queueRef.current = [];
  }, []);

  return {
    items,
    startedAt,
    running,
    startDownloads,
    cancel,
    dismiss,
    retry
  };
};

const CaseTable = ({ account = '' }) => {
  const { formatAmount } = useCurrency();

  const { hasPricingAccess } = usePricingAccess();

  const {
    items: uploadItems,
    kind: uploadKind,
    startedAt: uploadStartedAt,
    batchCaseId,
    busy: uploadBusy,
    startBatch,
    cancel: cancelGlobalUpload,
    dismiss: dismissGlobalUpload,
  } = useUploadManager();

  // ===== DOWNLOAD MANAGER =====
  const {
    items: downloadItems,
    startedAt: downloadStartedAt,
    running: downloadRunning,
    startDownloads,
    cancel: cancelDownloads,
    dismiss: dismissDownloads,
    retry: retryDownload,
  } = useDownloadManager();

  const ctx = useContext(CaseDataContext);
  const ctxCaseData = ctx ? ctx.caseData : null;
  const ctxLoading = ctx ? ctx.loading : false;
  const hasCtx = !!ctx;

  const caseId = useMemo(() => {
    if (ctx?.caseId) return ctx.caseId;

    const pathParts = window.location.pathname.split('/');
    const id = pathParts[pathParts.length - 1];

    return id || null;
  }, [ctx?.caseId]);

  const [caseData, setCaseData] = useState(ctxCaseData || null);
  const [loading, setLoading] = useState(!ctxCaseData);
  const [error, setError] = useState(null);

  const [fileOverrides, setFileOverrides] = useState({});

  const [currentProcedure, setCurrentProcedure] = useState(null);
  const [showImage, setShowImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // ===== MULTI-SELECT STATE =====
  const [selectedFileIds, setSelectedFileIds] = useState(() => new Set());
  const [modalFileUrls, setModalFileUrls] = useState(null);

  const [expandedRows, setExpandedRows] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEverOpened, setEditEverOpened] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [userLabId] = useState(getInitialLabId);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, openUp: false });

  const [uploadMinimized, setUploadMinimized] = useState(false);
  const [downloadMinimized, setDownloadMinimized] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);

  const isMobile = useMediaQuery('(max-width: 767px)');

  const getResponsiveFontSize = useCallback(
    (desktopSize, mobileSize) => rf(isMobile, desktopSize, mobileSize),
    [isMobile]
  );

  const latest = useRef({});
  latest.current = { uploadBusy, startBatch, caseId };

  const fileInputRef = useRef(null);

  const handleUploadClick = useCallback(() => {
    const input = fileInputRef.current;

    if (!input) {
      toast.error('Upload is not ready yet. Please try again.');
      return;
    }

    input.click();
  }, []);

  useEffect(() => {
    if (!userLabId) {
      console.warn(
        "No userLabId found. Please set it in localStorage or sessionStorage"
      );
    }
  }, [userLabId]);

  useEffect(() => {
    if (openDropdownId === null) return;

    const handleOutsideClick = (e) => {
      if (
        e.target.closest('[data-case-row-menu]') ||
        e.target.closest('[data-case-row-toggle]')
      ) {
        return;
      }
      setOpenDropdownId(null);
    };

    const handleReposition = () => setOpenDropdownId(null);

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [openDropdownId]);

  const fetchCaseDetails = useCallback(async (id, { silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);

      const caseResponse = await axios.get(
        `${API_BASE_URL}/api/cases/cases/${id}`,
        { withCredentials: true }
      );

      setCaseData(caseResponse.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching case details');

      console.error('Error fetching case details:', err);

      toast.error('Failed to fetch case details');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!caseId) return;

    if (hasCtx) {
      if (ctxCaseData) {
        setCaseData((prev) => prev || ctxCaseData);
        setLoading(false);
        return;
      }

      if (ctxLoading) return;
    }

    fetchCaseDetails(caseId);
  }, [caseId, hasCtx, ctxCaseData, ctxLoading, fetchCaseDetails]);

  const attachedFiles = caseData?.attachedFiles;

  useEffect(() => {
    setFileOverrides((prev) => (Object.keys(prev).length ? {} : prev));
  }, [attachedFiles]);

  const { images, stlFiles, otherFiles } = useMemo(() => {
    const imgs = [];
    const stls = [];
    const others = [];

    if (Array.isArray(attachedFiles)) {
      attachedFiles.forEach((f) => {
        if (!f || !f.path) return;

        const override = fileOverrides[f._id];
        const file = override ? { ...f, ...override } : f;

        if (/\.(png|jpg|jpeg)$/i.test(f.path)) {
          imgs.push(file);
        } else if (/\.stl$/i.test(f.path)) {
          stls.push(file);
        } else {
          others.push(file);
        }
      });
    }

    return { images: imgs, stlFiles: stls, otherFiles: others };
  }, [attachedFiles, fileOverrides]);

  // ===== MULTI-SELECT HELPERS =====
  const toggleFileSelection = useCallback((fileId) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);

  const clearFileSelection = useCallback(() => {
    setSelectedFileIds(new Set());
  }, []);

  const deselectAllFiles = useCallback(() => {
    setSelectedFileIds(new Set());
  }, []);

  const selectAllViewableFiles = useCallback(() => {
    const viewable = [...stlFiles, ...otherFiles].filter(
      (f) => /\.stl$/i.test(f.path) || /\.ply$/i.test(f.path)
    );
    setSelectedFileIds(new Set(viewable.map((f) => f._id)));
  }, [stlFiles, otherFiles]);

  const selectedViewableFiles = useMemo(() => {
    const all = [...stlFiles, ...otherFiles];
    return all.filter(
      (f) =>
        selectedFileIds.has(f._id) &&
        (/\.stl$/i.test(f.path) || /\.ply$/i.test(f.path))
    );
  }, [stlFiles, otherFiles, selectedFileIds]);

  const totalViewableFiles = useMemo(() => {
    const all = [...stlFiles, ...otherFiles];
    return all.filter(
      (f) => /\.stl$/i.test(f.path) || /\.ply$/i.test(f.path)
    );
  }, [stlFiles, otherFiles]);

  const allViewableSelected =
    totalViewableFiles.length > 0 &&
    selectedViewableFiles.length === totalViewableFiles.length;

  const handleOpenSelectedInViewer = useCallback(() => {
    if (selectedViewableFiles.length === 0) {
      toast.info('Select at least one STL or PLY file to view');
      return;
    }
    setSelectedFile(selectedViewableFiles[0]);
    setModalFileUrls(
      selectedViewableFiles.map((f) => ({
        url: getImageUrl(f.path),
        name: f.name,
        id: f._id,
      }))
    );
    setShowModal(true);
  }, [selectedViewableFiles]);

  useEffect(() => {
    setSelectedFileIds((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set([
        ...stlFiles.map((f) => f._id),
        ...otherFiles.filter((f) => /\.ply$/i.test(f.path)).map((f) => f._id),
      ]);
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [stlFiles, otherFiles]);

  const toggleRowExpansion = useCallback((procId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [procId]: !prev[procId]
    }));
  }, []);

  const openRowMenu = useCallback((procId, buttonEl) => {
    if (openDropdownId === procId) {
      setOpenDropdownId(null);
      return;
    }

    const rect = buttonEl.getBoundingClientRect();
    const menuWidth = isMobile ? 110 : 130;
    const menuHeightEstimate = 44;
    const margin = 8;

    let left = rect.right - menuWidth;
    left = Math.max(margin, Math.min(left, window.innerWidth - menuWidth - margin));

    let top = rect.bottom + 4;
    let openUp = false;

    if (top + menuHeightEstimate > window.innerHeight - margin) {
      top = rect.top - menuHeightEstimate - 4;
      openUp = true;
    }

    setDropdownPos({ top, left, openUp });
    setOpenDropdownId(procId);
  }, [openDropdownId, isMobile]);

  const handleEditProcedure = useCallback((proc) => {
    setCurrentProcedure(proc);
    setEditEverOpened(true);
    setShowEditModal(true);
  }, []);

  const handleOpenModal = useCallback((file) => {
    setSelectedFile(file);
    setModalFileUrls(null);
    setShowModal(true);
  }, []);

  const handleCloseStlModal = useCallback(() => {
    setShowModal(false);
    setModalFileUrls(null);
  }, []);

  const handleViewImage = useCallback((file) => {
    setShowImage(getImageUrl(file.path));
  }, []);

  const startUploadBatch = useCallback(
    async (files, kind, buildRequest, concurrency = 2) => {
      const { uploadBusy: busy, startBatch: start, caseId: id } = latest.current;

      if (busy) {
        toast.info('An upload is already in progress. Please wait for it to finish.');
        return;
      }

      setUploadMinimized(false);

      const results = await start(files, kind, buildRequest, concurrency);

      if (results.done > 0 && id) {
        await fetchCaseDetails(id, { silent: true });
      }

      const noun =
        kind === 'replace' ? 'File replaced' : `${results.done} file(s) uploaded`;

      if (results.failed === 0 && results.canceled === 0) {
        toast.success(`${noun} successfully!`);
      } else if (results.done > 0) {
        toast.warn(
          `${results.done} uploaded, ${results.failed} failed, ${results.canceled} canceled`
        );
      } else if (results.failed > 0) {
        toast.error(results.lastError || 'Upload failed');
      } else {
        toast.info('Upload canceled');
      }
    },
    [fetchCaseDetails]
  );

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (files.length === 0 || !caseId) return;

    if (uploadBusy) {
      toast.info('An upload is already in progress. Please wait for it to finish.');
      return;
    }

    await startUploadBatch(
      files,
      'upload',
      () => ({
        method: 'post',
        url:
          `${API_BASE_URL}/api/cases/${caseId}/upload` +
          `?uploadedBy=${encodeURIComponent(getUploaderName())}`,
        field: 'files',
        caseId,
      }),
      2
    );
  };

  const handleDelete = useCallback(async (fileId) => {
    const id = latest.current.caseId;

    if (window.confirm("Are you sure you want to delete this file?")) {
      try {
        await axios.delete(
          `${API_BASE_URL}/api/cases/${id}/files/${fileId}`,
          { withCredentials: true }
        );

        await fetchCaseDetails(id, { silent: true });

        toast.success('File deleted successfully');
      } catch (err) {
        console.error("Delete failed:", err);

        toast.error(
          `Failed to delete file: ${err.response?.data?.message || err.message}`
        );
      }
    }
  }, [fetchCaseDetails]);

  const handleDeleteOther = useCallback(async (fileId) => {
    const id = latest.current.caseId;

    if (window.confirm("Are you sure you want to delete this file?")) {
      try {
        await axios.delete(
          `${API_BASE_URL}/api/cases/${id}/files/${fileId}`,
          { withCredentials: true }
        );

        await fetchCaseDetails(id, { silent: true });

        toast.success('File deleted successfully');
      } catch (error) {
        console.error('Delete error:', error);

        toast.error(
          `Delete failed: ${error.response?.data?.message || error.message}`
        );
      }
    }
  }, [fetchCaseDetails]);

  const handleReplace = useCallback(async (fileId, file, fileType) => {
    const id = latest.current.caseId;

    if (!file || !id || !fileId) {
      toast.error("Invalid file or case information");
      return;
    }

    let validExtensions;

    if (fileType === 'image') {
      validExtensions = ['.png', '.jpg', '.jpeg'];
    } else if (fileType === 'stl') {
      validExtensions = ['.stl'];
    } else {
      validExtensions = ALLOWED_EXTENSIONS;
    }

    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validExtensions.includes(ext)) {
      toast.error(
        `Invalid file type. Please upload ${validExtensions.join(', ')} files.`
      );
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast.error("File size exceeds 100MB limit");
      return;
    }

    if (latest.current.uploadBusy) {
      toast.info('An upload is already in progress. Please wait for it to finish.');
      return;
    }

    await startUploadBatch(
      [file],
      'replace',
      () => ({
        method: 'put',
        url:
          `${API_BASE_URL}/api/cases/cases/${id}/files/${fileId}/replace` +
          `?uploadedBy=${encodeURIComponent(getUploaderName())}`,
        field: 'file',
        caseId: id,
      }),
      1
    );
  }, [startUploadBatch]);

  const togglePrivacy = useCallback(async (id, currentStatus) => {
    try {
      await axios.put(
        `${API_BASE_URL}/api/files/${id}/privacy`,
        { private: !currentStatus },
        { withCredentials: true }
      );

      setFileOverrides((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), private: !currentStatus }
      }));
    } catch (err) {
      console.error("Privacy toggle failed:", err);

      toast.error('Failed to toggle privacy');
    }
  }, []);

  /* ============================================================
     UNIFIED DOWNLOAD HANDLER
     - Uses DownloadManager for all files (images, stl, other)
     - Prevents duplicate/concurrent downloads of same file
     - Shows live progress modal
     ============================================================ */
  const handleDownloadFile = useCallback((file) => {
    if (!file || !file.path) {
      toast.error('Invalid file');
      return;
    }

    setDownloadMinimized(false);

    startDownloads([
      {
        file,
        url: getImageUrl(file.path)
      }
    ]);
  }, [startDownloads]);

  const handleRenameFile = useCallback(async (fileId, newName) => {
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/cases/${latest.current.caseId}/files/${fileId}/rename`,
        { name: newName },
        { withCredentials: true }
      );

      const savedName = res.data?.data?.name || newName;

      setFileOverrides((prev) => ({
        ...prev,
        [fileId]: {
          ...(prev[fileId] || {}),
          name: savedName,
          updatedAt: new Date().toISOString()
        }
      }));

      toast.success('File renamed successfully');
      return true;
    } catch (err) {
      console.error('Rename failed:', err);

      toast.error(
        `Rename failed: ${err.response?.data?.message || err.message}`
      );
      return false;
    }
  }, []);

  const handleRenameOpen = useCallback((file) => setRenameTarget(file), []);
  const handleRenameClose = useCallback(() => setRenameTarget(null), []);

  const handleSaveProcedure = useCallback(async (updatedProcedure) => {
    const id = latest.current.caseId;

    try {
      if (!id) {
        console.error('Case ID is missing');

        toast.error('Case ID is missing');

        return;
      }

      if (!updatedProcedure._id) {
        console.error('Procedure ID is missing');

        toast.error('Procedure ID is missing');

        return;
      }

      const requiredFields = ['procedure', 'teeth', 'shade'];

      const missingFields = requiredFields.filter(
        (field) => !updatedProcedure[field]?.trim()
      );

      if (missingFields.length > 0) {
        console.error('Missing required fields:', missingFields);

        toast.error(
          `Please fill in all required fields: ${missingFields.join(', ')}`
        );

        return;
      }

      const procedureData = {
        procedure: updatedProcedure.procedure.trim(),
        procedureId: updatedProcedure.procedureId,
        accountId: updatedProcedure.accountId,
        teeth: updatedProcedure.teeth.trim(),
        shade: updatedProcedure.shade.trim(),
        alloyMaterial: updatedProcedure.alloyMaterial?.trim() || '',
        contactsEmbrasures: updatedProcedure.contactsEmbrasures?.trim() || '',
        occlusalContact: updatedProcedure.occlusalContact?.trim() || '',
        insufficientRoom: updatedProcedure.insufficientRoom?.trim() || '',
        retention: updatedProcedure.retention?.trim() || '',
        marginPosition: updatedProcedure.marginPosition?.trim() || '',
        price: parseFloat(updatedProcedure.price) || 0
      };

      const response = await axios.put(
        `${API_BASE_URL}/api/cases/${id}/procedures/${updatedProcedure._id}`,
        procedureData,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true
        }
      );

      if (response.data.success) {
        setShowEditModal(false);

        await fetchCaseDetails(id, { silent: true });

        toast.success('Procedure updated successfully!');
      } else {
        console.error('Server responded with failure:', response.data.message);

        toast.error(response.data.message || 'Failed to update procedure');
      }
    } catch (err) {
      console.error('Error updating procedure:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });

      let errorMessage = 'Failed to update procedure';

      if (err.response) {
        if (err.response.data && err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.status === 400) {
          errorMessage = 'Bad request - please check your input';
        } else if (err.response.status === 404) {
          errorMessage = 'Case or procedure not found';
        } else if (err.response.status === 500) {
          errorMessage = 'Server error - please try again later';
        }
      }

      toast.error(errorMessage);
    }
  }, [fetchCaseDetails]);

  const handleDeleteProcedure = useCallback(async (procedureId) => {
    const id = latest.current.caseId;

    if (window.confirm("Are you sure you want to permanently delete this procedure?")) {
      setDeleting(true);

      try {
        const response = await axios.delete(
          `${API_BASE_URL}/api/cases/${id}/procedures/${procedureId}`,
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' }
          }
        );

        if (response.data.success) {
          setCaseData((prev) => ({
            ...prev,
            procedures: prev.procedures.filter((p) => p._id !== procedureId)
          }));

          toast.success('Procedure deleted successfully!');
        }
      } catch (err) {
        console.error('Error deleting procedure:', err);

        let errorMessage = 'Failed to delete procedure';

        if (err.response) {
          if (err.response.status === 404) {
            errorMessage = err.response.data?.message || 'Procedure not found';
          } else if (err.response.data?.message) {
            errorMessage = err.response.data.message;
          }
        }

        toast.error(errorMessage);
      } finally {
        setDeleting(false);
      }
    }
  }, []);

  const handleRowDelete = useCallback((procedureId) => {
    setOpenDropdownId(null);
    handleDeleteProcedure(procedureId);
  }, [handleDeleteProcedure]);

  const handleProcedureAdded = useCallback(() => {
    fetchCaseDetails(latest.current.caseId, { silent: true });
  }, [fetchCaseDetails]);

  const handleComponentsUpdate = useCallback((updatedComponents) => {
    setCaseData((prev) => ({
      ...prev,
      components: updatedComponents
    }));
  }, []);

  const handleCloseEditModal = useCallback(() => setShowEditModal(false), []);

  const handleToggleUploadMinimized = useCallback(
    () => setUploadMinimized((m) => !m),
    []
  );

  const handleToggleDownloadMinimized = useCallback(
    () => setDownloadMinimized((m) => !m),
    []
  );

  const getProcedurePrice = (procedure) => {
    if (!hasPricingAccess) return null;
    const storedPrice = procedure.price || 0;
    return formatAmount(storedPrice);
  };

  const showInPageUploadPanel =
    uploadItems.length > 0 && batchCaseId && batchCaseId === caseId;

  const showInPageDownloadPanel = downloadItems.length > 0;

  const g = getResponsiveFontSize;

  if (loading)
    return (
      <div style={{ fontSize: g('16px', '11px') }}>
        Loading case details...
      </div>
    );

  if (error)
    return (
      <div style={{ fontSize: g('16px', '11px') }}>
        Error: {error}
      </div>
    );

  if (!caseData)
    return (
      <div style={{ fontSize: g('16px', '11px') }}>
        No case data found
      </div>
    );

  return (
    <div
      style={{
        width: "100%",
        fontSize: g('16px', '11px')
      }}
    >
      <style>{ALL_STYLES}</style>

      {/* WORK REQUESTED HEADER */}
      <div
        className="row work-requested-header"
        style={{
          backgroundColor: '#004aad',
          padding: g('7px', '4px'),
          borderRadius: "8px",
          width: '100%',
          margin: '0',
          marginTop: '10px'
        }}
      >
        <div
          className="col work-requested-title-container"
          style={{ textAlign: "left", width: "100%" }}
        >
          <h2
            className="work-requested-title"
            style={{
              color: 'white',
              margin: 0,
              fontSize: g('32px', '14px')
            }}
          >
            Work Requested
          </h2>
        </div>

        <div
          className="col text-end work-requested-button-container"
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center'
          }}
        >
          <div
            className="work-requested-procedure-wrapper"
            style={{
              display: 'inline-block',
              fontSize: g('14px', '7px'),
              transform: isMobile ? 'scale(0.8)' : 'scale(1)',
              transformOrigin: 'right center'
            }}
          >
            <MemoProcedureModel
              caseId={caseId}
              account={account}
              onProcedureAdded={handleProcedureAdded}
            />
          </div>
        </div>
      </div>

      {/* PROCEDURE TABLE */}
      <div className="row mt-3 procedure-table-wrap" style={{ overflowX: "auto" }}>
        <table
          className="table procedure-table"
          style={{
            fontSize: g("16px", "10px"),
            width: "100%",
            tableLayout: "auto",
          }}
        >
          <thead
            style={{
              backgroundColor: "#f7f7f7",
              borderBottom: "1px solid #dee2e6",
            }}
          >
            <tr className="align-middle">
              <th className="pt-col-units"
                style={{
                  width: "8%",
                  fontWeight: "700",
                  fontSize: g("16px", "10px"),
                  whiteSpace: "nowrap",
                }}
              >
                Units
              </th>

              <th
                style={{
                  fontWeight: "700",
                  fontSize: g("16px", "10px"),
                  whiteSpace: "nowrap",
                }}
              >
                Description
              </th>

              {hasPricingAccess && (
                <th className="pt-col-price"
                  style={{
                    textAlign: "right",
                    width: "15%",
                    fontWeight: "700",
                    fontSize: g("16px", "10px"),
                    whiteSpace: "nowrap",
                  }}
                >
                  Price
                </th>
              )}

              <th className="pt-col-actions"
                style={{
                  width: "10%",
                  minWidth: g("75px", "65px"),
                }}
              ></th>
            </tr>
          </thead>

          <tbody>
            {caseData.procedures && caseData.procedures.length > 0 ? (
              caseData.procedures.map((proc, index) => {
                const procId = proc._id || proc.key || index;

                return (
                  <ProcedureRow
                    key={procId}
                    proc={proc}
                    procId={procId}
                    expanded={!!expandedRows[procId]}
                    isMobile={isMobile}
                    hasPricingAccess={hasPricingAccess}
                    priceText={getProcedurePrice(proc)}
                    menuPos={openDropdownId === procId ? dropdownPos : null}
                    deleting={deleting}
                    onToggleExpand={toggleRowExpansion}
                    onEdit={handleEditProcedure}
                    onOpenMenu={openRowMenu}
                    onDeleteProcedure={handleRowDelete}
                  />
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={hasPricingAccess ? 4 : 3}
                  className="text-center"
                  style={{ fontSize: g("16px", "10px") }}
                >
                  No procedures requested yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editEverOpened && (
        <EditProcedureModal
          show={showEditModal}
          handleClose={handleCloseEditModal}
          procedure={currentProcedure}
          onProcedureUpdated={handleSaveProcedure}
          account={account}
          accountHolder={caseData?.account_holder}
          caseId={caseId}
          userLabId={userLabId}
          hasPricingAccess={hasPricingAccess}
        />
      )}

      {/* Components */}
      <div style={{ display: "flex", gap: "1%", alignItems: "center" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: g('8px', '2px')
          }}
        >
          <MdWindow
            style={{
              fontSize: g('3rem', '1.2rem'),
              textAlign: "left",
              marginLeft: "1px",
              padding: g('0', '0')
            }}
          />

          <strong
            style={{
              fontSize: g('30px', '11px'),
              fontWeight: 'normal'
            }}
          >
            Components
          </strong>
        </div>

        <MemoComTable
          caseId={caseId}
          currentComponents={caseData?.components || EMPTY_ARRAY}
          onUpdate={handleComponentsUpdate}
        />
      </div>

      {/* Enclosures */}
      <div
        style={{
          display: "flex",
          gap: "1.8%",
          alignItems: "center",
          marginTop: "5px"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: g('8px', '2px')
          }}
        >
          <GoInbox
            style={{
              fontSize: g('2.5rem', '1.1rem'),
              textAlign: "left",
              padding: g('0', '0')
            }}
          />

          <strong
            style={{
              fontSize: g('30px', '11px'),
              fontWeight: 'normal'
            }}
          >
            Enclosures
          </strong>
        </div>

        <MemoEnoTable caseId={caseId} enclosures={caseData.enclosures || EMPTY_ARRAY} />
      </div>

      {/* Upload Case Files */}
      <div
        className="upload-container"
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: g('10px', '3px'),
          padding: '10px 0',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <FaRegCopy style={{ fontSize: g('2.5rem', '1.2rem') }} />

        <span
          style={{
            fontSize: g('30px', '11px'),
            fontWeight: 'normal'
          }}
        >
          Upload Case Files
        </span>

        <input
          type="file"
          id="caseTableFileInput"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept={ALLOWED_EXTENSIONS.join(',')}
          multiple
          onChange={handleFileChange}
        />

        <button
          onClick={handleUploadClick}
          style={{
            backgroundColor: '#004aad',
            color: 'white',
            border: 'none',
            padding: g('8px 24px', '3px 8px'),
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: g('16px', '9px'),
            fontWeight: '600',
            marginLeft: 'auto',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 8px rgba(0, 74, 173, 0.3)',
            letterSpacing: '0.5px',
            textTransform: 'uppercase'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#003a8a';
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 4px 12px rgba(0, 74, 173, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#004aad';
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 2px 8px rgba(0, 74, 173, 0.3)';
          }}
        >
          ⬆ Upload
        </button>
      </div>

      {/* Image Files */}
      <div>
        <div
          style={{
            backgroundColor: 'lightblue',
            padding: g('15px', '6px'),
            borderRadius: '5px',
            fontWeight: 'bold',
            fontSize: g('19px', '11px'),
            marginTop: '20px'
          }}
        >
          Image Files
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: g('15px', '6px'),
            marginTop: '10px'
          }}
        >
          {images.length > 0 ? (
            images.map((image) => (
              <FileCard
                key={image._id}
                file={image}
                variant="image"
                isMobile={isMobile}
                onDownload={handleDownloadFile}
                onView={handleViewImage}
                onDelete={handleDelete}
                onReplace={handleReplace}
                onRename={handleRenameOpen}
                onTogglePrivacy={togglePrivacy}
              />
            ))
          ) : (
            <p style={{ fontSize: g('16px', '10px') }}>
              No images found
            </p>
          )}
        </div>

        {showImage && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              backgroundColor: 'rgba(0, 0, 0, 0.7)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowImage(null);
            }}
          >
            <img
              src={showImage}
              alt="Fullscreen"
              onError={handleImgError}
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain'
              }}
            />
          </div>
        )}
      </div>

      <br />

      {/* STL - OPTIMIZED LAZY SECTION */}
      <div
        style={{
          backgroundColor: "lightblue",
          padding: g('15px', '6px'),
          fontWeight: "bold",
          marginTop: "20px",
          fontSize: g('19px', '11px'),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          flexWrap: "wrap"
        }}
      >
        <span>Intraoral Scan Files</span>

        {selectedViewableFiles.length > 0 && (
          <div
            style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
            onClick={(e) => e.stopPropagation()}
          >
            <span
              style={{
                fontSize: g('13px', '10px'),
                fontWeight: 600,
                color: '#004aad'
              }}
            >
              {selectedViewableFiles.length} selected
            </span>

            <button
              type="button"
              onClick={handleOpenSelectedInViewer}
              style={{
                backgroundColor: '#004aad',
                color: 'white',
                border: 'none',
                padding: g('6px 14px', '4px 8px'),
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: g('13px', '9px'),
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 2px 6px rgba(0,74,173,0.3)'
              }}
            >
              Open in STL Viewer
            </button>

            {!allViewableSelected && totalViewableFiles.length > 0 && (
              <button
                type="button"
                onClick={selectAllViewableFiles}
                style={{
                  backgroundColor: '#fff',
                  color: '#004aad',
                  border: '1px solid #004aad',
                  padding: g('6px 10px', '4px 8px'),
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: g('13px', '9px'),
                  fontWeight: 600
                }}
                title="Select all STL/PLY files"
              >
                Select All
              </button>
            )}

            <button
              type="button"
              onClick={deselectAllFiles}
              style={{
                backgroundColor: '#fff',
                color: '#c93636',
                border: '1px solid #f0c4c4',
                padding: g('6px 10px', '4px 8px'),
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: g('13px', '9px'),
                fontWeight: 600
              }}
              title="Deselect all selected files"
            >
              Deselect All
            </button>
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: g('15px', '6px'),
          marginTop: "10px"
        }}
      >
        {stlFiles.length > 0 ? (
          stlFiles.map((file) => (
            <FileCard
              key={file._id}
              file={file}
              variant="stl"
              isMobile={isMobile}
              onDownload={handleDownloadFile}
              onView={handleOpenModal}
              onDelete={handleDelete}
              onReplace={handleReplace}
              onRename={handleRenameOpen}
              onTogglePrivacy={togglePrivacy}
              selectable
              selected={selectedFileIds.has(file._id)}
              onToggleSelect={toggleFileSelection}
            />
          ))
        ) : (
          <p style={{ fontSize: g('16px', '10px') }}>
            No STL files found
          </p>
        )}
      </div>

      {showModal && selectedFile && (
        <STLFullViewModal
          file={selectedFile}
          files={selectedViewableFiles}
          fileUrl={getImageUrl(selectedFile.path)}
          fileUrls={modalFileUrls}
          onClose={handleCloseStlModal}
          getResponsiveFontSize={getResponsiveFontSize}
        />
      )}

      {/* Other Files */}
      <div
        style={{
          backgroundColor: "lightblue",
          padding: g('15px', '6px'),
          fontWeight: "bold",
          marginTop: "20px",
          fontSize: g('19px', '11px')
        }}
      >
        Other Files
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: g('15px', '6px'),
          marginTop: "10px"
        }}
      >
        {otherFiles.length > 0 ? (
          otherFiles.map((file) => {
            const isPly = isPlyFile(file);
            return (
              <FileCard
                key={file._id}
                file={file}
                variant="other"
                isMobile={isMobile}
                onDownload={handleDownloadFile}
                onView={handleOpenModal}
                onDelete={handleDeleteOther}
                onReplace={handleReplace}
                onRename={handleRenameOpen}
                onTogglePrivacy={togglePrivacy}
                selectable={isPly}
                selected={isPly && selectedFileIds.has(file._id)}
                onToggleSelect={toggleFileSelection}
              />
            );
          })
        ) : (
          <p style={{ fontSize: g('16px', '10px') }}>
            No files found
          </p>
        )}
      </div>

      {showInPageUploadPanel && (
        <UploadProgressPanel
          items={uploadItems}
          kind={uploadKind}
          startedAt={uploadStartedAt}
          minimized={uploadMinimized}
          onToggle={handleToggleUploadMinimized}
          onCancel={cancelGlobalUpload}
          onClose={dismissGlobalUpload}
        />
      )}

      {showInPageDownloadPanel && (
        <DownloadProgressPanel
          items={downloadItems}
          startedAt={downloadStartedAt}
          minimized={downloadMinimized}
          onToggle={handleToggleDownloadMinimized}
          onCancel={cancelDownloads}
          onClose={dismissDownloads}
          onRetry={retryDownload}
        />
      )}

      <RenameFileModal
        show={!!renameTarget}
        file={renameTarget}
        onClose={handleRenameClose}
        onSave={handleRenameFile}
      />
    </div>
  );
};

export default CaseTable;